import { Component, Input, ViewChild, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule, KeyValue } from '@angular/common';
import { ConfirmationService, MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { of, Subscription } from 'rxjs';
import { concatMap, take } from 'rxjs/operators';
import { ActivatedRoute, Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { UserNavigationService } from './services/user-navigation.service';
import { UserNavigationResult } from './models/user-navigation-result.model';
import { NavigationItem } from './models/navigation-item.model';
import { environment } from 'src/environments/environment';
import { Breadcrumb } from './models/breadcrumb.model';
import { UserNavigation } from './models/user-navigation.model';
import { AuthService } from './services/auth.service';
import { LoggingMessageService } from './services/logging-message.service';
import { SessionStorageService } from './services/session-storage.service';
import { SlideMenu, SlideMenuSub, SlideMenuModule } from 'primeng/slidemenu';
import { NavigationMenuTypeEnum } from './models/navigation-menu-type-enum';
import { UserNavigationMappingService } from './services/user-navigation-mapping.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { IframeService } from './services/iframe.service';
import { DomSanitizer } from '@angular/platform-browser';
import { IframeModel } from './models/iframe.model';
import { HospitalChangeService } from './services/hospital-change.service';
import { DataResult } from './models/data-result.mode';
import { WindowEventsService } from './services/window-events.service';
import { PendoService } from './services/pendo.service';
import { BreadcrumbService } from './services/breadcrumb.service';
import { FederationService } from './services/federation.service';
import { UserRegistrationService } from './services/user-registration.service';
import { IndicatorResolverService } from './services/indicator-resolver.service';
import { SubscriptionChangeService } from './services/subscription-change.service';
import { DemographicContentService } from './services/demographic-content.service';
import { PermIdIndexerEnum } from './models/permid-indexer-enum';
import { NavigationUtils } from './navigation-utils';
import { SubDomainEnum } from './models/subdomain.enum';
import { MenuService } from './services/menu-service.service';
import { TimeoutService } from './services/timeout-service.service';
import { PgFooterComponent } from './components/footer/pg-footer.component';
import { GlobalHeaderComponent } from './components/global-header/global-header.component';
import { DemographicModalComponent } from './components/demographic-modal/demographic-modal.component';
import { PgGlobalHeaderComponent } from './components/global-header/pg-global-header';
import { GlobalHeaderMenuService } from './services/global-header-menu.service';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    CommonModule,
    ConfirmDialogModule,
    DemographicModalComponent,
    GlobalHeaderComponent,
    PgGlobalHeaderComponent,
    PgFooterComponent,
    RouterOutlet,
    SlideMenuModule
  ],
  providers: [
    ConfirmationService
  ]
})

export class AppComponent implements OnInit, OnDestroy {
  // One-time marker used to detect an actual browser refresh (F5 / reload button).
  private readonly _RELOAD_TO_HOME_KEY = 'ndnqi-wrapper-reload-to-home';
  @Input() public showMenu = false;
  @ViewChild('slidemenu') slideMenu!: SlideMenu;
  private subMenu: SlideMenuSub;
  public title = 'ndnqiwrapper';
  private $subscriptions: Subscription = new Subscription();
  private activeLinkId: string;
  private finalDestination: string;
  public applicationVersion = 'n0.0';
  public menuHeader = 'System';
  private _priorToTimeoutUrl: string = '';
  private _priorToTimeoutVisibility: SubDomainEnum = SubDomainEnum.NotSet;
  public isUserLoggedIn = false;
  public isFederationLoaded = false;
  public enableGlobalNavigationHeader = environment.enableGlobalNavigationHeader;
  public isGlobalNavHxUser = false;
  private _shouldShowSharedGlobalHeader = false;
  public settingMenuItems = false;
  public settingMenuPositioning = false;
  public portalClass = '';
  private PORTAL_REPORTS = 'qidashboard.aspx';
  public displayChildItems: boolean;
  public user: string;
  private backButtonClicked = false;
  public menuLoadError = false;
  public hospitalLoadError = false;
  public indicatorFrameProp: IframeModel;
  public portalFrameProp: IframeModel;
  public validationFrameProp: IframeModel;
  public performanceFrameProp: IframeModel;
  public sysMngFrameProp: IframeModel;
  public learningMngFrameProp: IframeModel;
  public ndnqiPublicReportsFrameProp: IframeModel;
  public ndnqiFoundationFrameProp: IframeModel;
  public demographicContent: any;
  public subscriptionChangeError = false;
  public displayProductSelector = false;
  public timeoutCounter = 0;
  public timeoutCounterDisplayEnabled = false;
  private availableSubscriptions = new Array<string>();
  private subscriptions = '';
  private _NO_DATA = { data: [] };
  public isDevOrLocalEnv = (environment.environment === 'DEV' || environment.environment === 'Local');
  public swAppVersion: string;
  private subscription = 0;
  public menuDataLoaded: boolean = false;
  public menuAnimating: boolean = false;
 // private _gnFetchedOnce = false;

  get items(): MenuItem[] {
    return this.slideMenu.model;
  }

  set items(itemArray: MenuItem[]) {
    this.navigationService.updateDisplayedMenuItems(itemArray);
    this.slideMenu.model = itemArray;
  }

  get subscriptionSelected(): number {
    return this.subscription;
  }

  set subscriptionSelected(value: number) {
    this.subscription = value;
  }

  get subscriptionSelectedPermId(): string {
    let permId = '';
    permId = this.navigationMappingService.mapSubscriptionIdToPermId(this.navigationService.CachedNavigationMenu, this.subscriptionSelected);
    return permId;
  }

  private isMultiSubscription = false;

  get isMultiSubscriptionUser(): boolean {
    return this.isMultiSubscription;
  }

  set isMultiSubscriptionUser(value: boolean) {
    this.isMultiSubscription = value;
  }

  private _timeoutTokenRequested = false;

  public get IsTimeoutDialogVisible(): boolean {
      return this._timeoutTokenRequested;
  }

  public set IsTimeoutDialogVisible(value: boolean) {
      this._timeoutTokenRequested = value;
  }

  constructor(
    private router: Router,
    private menuService: MenuService,
    private navigationService: UserNavigationService,
    private navigationMappingService: UserNavigationMappingService,
    private userRegistrationService: UserRegistrationService,
    private authService: AuthService,
    private federationService: FederationService,
    private route: ActivatedRoute,
    private sessionService: SessionStorageService,
    private logger: LoggingMessageService,
    private deviceService: DeviceDetectorService,
    private sanitizer: DomSanitizer,
    private iframeService: IframeService,
    private hospitalChangeService: HospitalChangeService,
    private subscriptionChangeService: SubscriptionChangeService,
    private windowEvents: WindowEventsService,
    private pendoService: PendoService,
    private breadcrumbService: BreadcrumbService,
    private indicatorResolverService: IndicatorResolverService,
    private confirmationService: ConfirmationService,
    private timeoutService: TimeoutService,
    private demographicContentService: DemographicContentService,
    private globalHeaderMenu: GlobalHeaderMenuService
  ) {
    const favicon = document.getElementById('favicon');
    if (favicon) {
      favicon['href'] = "assets/images/favicon.ico";
    }
    const $navigationSubscription = this.router.events
      .subscribe((e: any) => {
        // If it is a NavigationEnd event display the report
        if (e instanceof NavigationEnd) {
          this.handleNavigationEvent(e);
        }
      });
    this.$subscriptions.add($navigationSubscription);
    const reRouteSubscription = this.windowEvents.portalRerouteSource$
      .subscribe(link => {
        link = link.replace('/if', '');
        this.router.navigateByUrl(`if${link}`);
      });
    this.$subscriptions.add(reRouteSubscription);
    const defaultIndicatorUrl = `${environment.IndicatorWebServer}/`;
    this.indicatorFrameProp = {
      src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultIndicatorUrl),
      srcString: defaultIndicatorUrl,
      visibility: false
    };
    const defaultPortalUrl = environment.PortalWebServer;
    this.portalFrameProp = {
      src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultPortalUrl),
      srcString: defaultPortalUrl,
      visibility: false
    };
    const defaultValidationUrl = `${environment.ValidationWebServer}/`;
    this.validationFrameProp = {
      src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultValidationUrl),
      srcString: defaultValidationUrl,
      visibility: false
    };
    const defaultPerformanceUrl = `${environment.PerformanceWebServer}/`;
    this.performanceFrameProp = {
      src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultPerformanceUrl),
      srcString: defaultPerformanceUrl,
      visibility: false
    };
    const defaultSysMngUrl = `${environment.SystemManagementWebServer}/`;
    this.sysMngFrameProp = {
      src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultSysMngUrl),
      srcString: defaultSysMngUrl,
      visibility: false
    };
    const defaultndnqiPublicReportsUrl = `${environment.NdnqiPublicReportsWebServer}/`;
    this.ndnqiPublicReportsFrameProp = {
      src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultndnqiPublicReportsUrl),
      srcString: defaultndnqiPublicReportsUrl,
      visibility: false
    };
    const defaultNdnqiFoundationUrl = `${environment.NdnqiFoundationWebServer}/`;
    this.ndnqiFoundationFrameProp = {
      src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultNdnqiFoundationUrl),
      srcString: defaultNdnqiFoundationUrl,
      visibility: false
    };
    if (this.isDevOrLocalEnv) {
      const defaultLearningMngUrl = `${environment.LearningManagementWebServer}/`;
      this.learningMngFrameProp = {
        src: this.sanitizer.bypassSecurityTrustResourceUrl(defaultLearningMngUrl),
        srcString: defaultLearningMngUrl,
        visibility: false
      };
    }
    const publicReportsRouteSubscription = this.windowEvents.publicReportReroute$
      .subscribe(link => {
        const urlParts = link.split('?');
        const path = urlParts[0];
        const url = `if/ndnqipublicreports/reports/single${path}`;
        const navExtras = this.mapQueryParams(urlParts);
        this.navigateToSameDomain(url, navExtras);
      });
    this.$subscriptions.add(publicReportsRouteSubscription);
    const publicReportPidReReouteSub = this.windowEvents.publicReportPidReReoute$
      .subscribe(navItem => {
        if (navItem) {
          this.activeLinkId = navItem.PermItemID;
          setTimeout(() => {
            this.onLinkClicked(navItem);
          }, 0);
        }
      });
    this.$subscriptions.add(publicReportPidReReouteSub);
    const pirRouteSubscription = this.windowEvents.pirReReoute$
      .subscribe(link => {
        const urlParts = link.split('?');
        const navExtras = this.mapQueryParams(urlParts);
        const linkid = navExtras.queryParams.linkid;
        const cachedUserNav = this.navigationService.CachedNavigationMenu;
        const navItem = cachedUserNav.getNavigationItemUsingPermID(linkid);
        this.routeToPirResolver(navItem, navExtras);
      });
    this.$subscriptions.add(pirRouteSubscription);
    // Facility Demographic modal
    if (environment.EnableFacilityDemographicsModal) {
      const demographicSub = this.demographicContentService.currentDemographicData$
        .subscribe(item => {
          this.demographicContent = item;
        });
      this.$subscriptions.add(demographicSub);
    }

    const portalSessionSubscription = this.windowEvents.portalSession$
    .subscribe(() => {
      this.logger.info('app.component', 'portalSessionSubscription', this.subscriptionSelected, this.subscriptions, `Portal Session Page Loaded`, '');
      if (this.IsTimeoutDialogVisible) {
        this.logger.info('app.component', 'portalSessionSubscription', this.subscriptionSelected, this.subscriptions, `Display Timeout Dialog`, '');
        this.displayTimeoutDialog();
      }
    });
    this.$subscriptions.add(portalSessionSubscription);
  }

  ngAfterViewInit() {
    const menu = this.slideMenu;
    const { nativeElement } = menu.el;
    this.menuService.menu = menu;
    this.subMenu = menu.rootmenu;
    menu.renderer.listen(nativeElement, 'click', (e: Event) => { this.slideMenuClick(e); });
    menu.renderer.listen(nativeElement, 'transitionstart', (e: TransitionEvent) => { this.onMenuTransitionStart(e); });
    menu.renderer.listen(nativeElement, 'transitionend', (e: TransitionEvent) => { this.onMenuTransitionEnd(e); });
  }

  get menuLoading(): boolean {
    const loading = this.settingMenuItems || this.settingMenuPositioning;
    const menu = this.slideMenu;
    if (loading) {
      this.menuDataLoaded = true;
    }
    if (this.menuDataLoaded && !loading && menu && !menu.visible) {
      setTimeout(() => menu.show(new Event('init')), 0);
      menu.visible = true;
    }
    return loading;
  }

  private recalcShouldShowSharedGlobalHeader(): void {
    const email = (this.user || this.authService.getTokenEmail() || '').toLowerCase();
    this._shouldShowSharedGlobalHeader = this.enableGlobalNavigationHeader && this.isGlobalNavHxUser;
    if (this._shouldShowSharedGlobalHeader) {
      this.logger.info('app.component', 'recalcShouldShowSharedGlobalHeader', this.subscriptionSelected, this.subscriptions,
        'User has signed in from HX ', `|hx=${this.isGlobalNavHxUser}|email=${email}`);
    }
  }

  public get shouldShowSharedGlobalHeader(): boolean {
    return this._shouldShowSharedGlobalHeader;
  }

  private mapQueryParams(urlParts: string[]): any {
    const urlParams = {};
    if (urlParts && urlParts.length > 1) {
      const queryPairs = urlParts[1].split('&');
      queryPairs.forEach(pair => {
        const pairParts = pair.split('=');
        urlParams[pairParts[0]] = pairParts[1];
      });
    }
    const navigationExtras = {
      queryParams: urlParams
    };
    return navigationExtras;
  }

  private handleNavigationEvent(e: NavigationEnd) {
    const eventUrl = (e.urlAfterRedirects || e.url || '').toLowerCase();
    // In case user does a hard refresh (F5, Ctrl+F5, etc.), check if we need to redirect to the beginning route.
    if (this.handlePageReload(eventUrl)) {
      return;
    }

    const linkId = this.route.snapshot.queryParams.linkid;
    if (linkId) {
      this.isUserLoggedIn = this.authService.isUserLoggedIn();
      this.logger.info('app.component', 'handleNavigationEvent', this.subscriptionSelected, this.subscriptions, `isUserLoggedIn=${this.isUserLoggedIn}`, '');
      const isMenuDisplayed = (this.navigationService.DisplayedNavigationMenu) ? true : false;
      if ((this.isUserLoggedIn === true) && (isMenuDisplayed === false)) {
        this.logger.info('app.component', 'handleNavigationEvent', this.subscriptionSelected, this.subscriptions, 'Bookmark used or page refresh clicked', '');
        // Bookmark used or page refresh clicked
        this.settingMenuItems = true;
        const hospId = NavigationUtils.GetHospitalIdFromPermId(linkId);
        if ((hospId) && (hospId.length > 0)) {
          // Load that hospital menu
          this.loadUserNavigationSingleHospitalMenu(hospId);
          this.activeLinkId = linkId;
        } else {
          // Bookmarked above hospital. Home or System Reports
          this.loadUserNavigationMenusBySubscription();
          this.activeLinkId = linkId;
        }
      }
    }
  }

  private handlePageReload(eventUrl: string): boolean {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const navType = navEntry ? navEntry.type : 'unknown';
    const reloadMarkerValue = sessionStorage.getItem(this._RELOAD_TO_HOME_KEY);
    // "Beginning" route requested by user for refresh behavior.
    const onBeginningRoute = eventUrl.includes('/if/indicatorfactory/home') && this.route.snapshot.queryParams.linkid === environment.HomePermId;
    // True only if this navigation came right after a hard browser reload.
    const shouldReturnToBeginning = reloadMarkerValue === '1';
    if (shouldReturnToBeginning && !onBeginningRoute) {
      // Consume marker immediately so we redirect only once per reload.
      sessionStorage.removeItem(this._RELOAD_TO_HOME_KEY);
      this.logger.info('app.component', 'handlePageReload', this.subscriptionSelected, this.subscriptions,
        `Browser reload detected. Redirecting to beginning route. Url=${eventUrl}`, '');
      // Preserve existing app behavior for normal navigation; only reload gets rerouted.
      this.router.navigate(['if/indicatorfactory/home'], {
        queryParams: { linkid: environment.HomePermId },
        replaceUrl: true
      });
      return true;
    }
    if (shouldReturnToBeginning) {
      // If already on beginning route, just clear marker and continue normal flow.
      sessionStorage.removeItem(this._RELOAD_TO_HOME_KEY);
    }
    return false;
  }

  ngOnInit() {
    this.logger.debug('app.component', 'ngOnInit', this.subscriptionSelected, this.subscriptions, 'fired', '');
    this.applicationVersion = environment.applicationVersion;
    this.isUserLoggedIn = this.authService.isUserLoggedIn();
    this.logger.info('app.component', 'ngOnInit', this.subscriptionSelected, this.subscriptions, `isUserLoggedIn=${this.isUserLoggedIn}`, '');
    this.user = this.authService.getTokenEmail();
    this.timeoutCounterDisplayEnabled = environment.TimeoutCounterDisplayEnabled;
    this.navigationMappingService.MenuItemCallback = this.onMenuItemClicked;
    this.navigationMappingService.Webserver = environment.WebServer;
    if (environment.production !== true) {
      this.authService.getNewAccessToken(environment.TokenServiceApi, environment.TokenServiceMedia, environment.UseTestToken, environment.UseCookies, environment.CookieName);
    }
    this.subscribeIndicatorVersionEvents();
    this.subscribeSysManVersionEvents();
    this.subscribePerfManVersionEvents();
    this.subscribeValEngineVersionEvents();
    this.subscribePubRepVersionEvents();
    this.subscribeToIFrameService();
    this.subscribeToUserLoginStatusUpdates();
    //this.subscribeGlobalNavUserInfo();
    this.subscribeToNavigationChanges();
    this.subscribeToBreadcrumbEvents();
    this.subscribeToTimeoutDecisionRequired();
    this.subscribeToTimeoutDecisionExpired();
  }

  public indicatorIframeLoaded() {
    this.logger.info('app.component', 'indicatorIframeLoaded', this.subscriptionSelected, this.subscriptions, 'IndicatorFactory iframe Loaded', '');
    this.iframeService.onIndicatorFrameLoaded();
  }

  public portalIframeLoaded() {
    this.logger.info('app.component', 'portalIframeLoaded', this.subscriptionSelected, this.subscriptions, 'Portal iframe Loaded', '');
    this.iframeService.onPortalFrameLoaded();
  }

  public validationIframeLoaded() {
    this.logger.info('app.component', 'validationIframeLoaded', this.subscriptionSelected, this.subscriptions, 'Validation iframe Loaded', '');
    this.iframeService.onValidationFrameLoaded();
  }

  public performanceIframeLoaded() {
    this.logger.info('app.component', 'performanceIframeLoaded', this.subscriptionSelected, this.subscriptions, 'Performance iframe Loaded', '');
    this.iframeService.onPerformanceFrameLoaded();
  }

  public sysMngIframeLoaded() {
    this.iframeService.onSystemManagementFrameLoaded();
  }

  public learningMngIframeLoaded() {
    this.iframeService.onLearningManagementFrameLoaded();
  }

  public ndnqiPublicReportsIframeLoaded() {
    this.logger.info('app.component', 'ndnqiPublicReportsIframeLoaded', this.subscriptionSelected, this.subscriptions, 'PublicReports iframe Loaded', '');
    this.iframeService.onPublicReportsFrameLoaded();
  }

  public ndnqiFoundationIframeLoaded() {
    this.logger.info('app.component', 'ndnqiFoundationIframeLoaded', this.subscriptionSelected, this.subscriptions, 'NdnqiFoundation iframe Loaded', '');
    this.iframeService.onNdnqiFoundationFrameLoaded();
  }

  // This check needs to be made because the hospital dashboard requires an async call to update the hospid in the session. As a result
  // it can load more slowly. When breadcrumbs are clicked the site traverses through the menu hitting a number of pages before it reaches it's destination.
  // This check ensures that it is the destination page that's loaded into the iframe.
  private isPageLoadRequired(newPath: string, oldPath: string): boolean {
    const pathParts = newPath.split('linkid=');
    const loadingLink = this.iframeService.LoadingLinkId;
    const hospIndex = PermIdIndexerEnum.Hospital as number;
    const isFinalDestination = (!loadingLink) || (pathParts.length < hospIndex) || (pathParts[1] === loadingLink);
    const isStub = loadingLink.includes('-stub');
    const isPirResolver = newPath.includes('pirresolver');
    const isNewSource = oldPath !== newPath;
    const required = (isNewSource && isFinalDestination) || isPirResolver || isStub;
    return required;
  }

  private subscribeGlobalNavUserInfo() {
    const gnSub = this.globalHeaderMenu.fetchGlobalNavUserInfo().pipe(take(1)).subscribe({
      next: () => {
        this.isGlobalNavHxUser = this.globalHeaderMenu.isGlobalNavHxUser();
        this.recalcShouldShowSharedGlobalHeader();
      },
      error: (e) => {
        this.logger.error('app.component', 'subscribeGlobalNavUserInfo', this.subscriptionSelected, this.subscriptions, 'fetch error', (e as any)?.message || '');
      }
    });
    this.$subscriptions.add(gnSub);
}
  private subscribeIndicatorVersionEvents() {
    const indicatorVersionSub = this.windowEvents.indicatorVersion$
      .subscribe(ifv => {
        this.applicationVersion += ifv;
      });
    this.$subscriptions.add(indicatorVersionSub);
  }

  private subscribeSysManVersionEvents() {
    const sysManVersionSub = this.windowEvents.sysManVersion$
      .subscribe(smv => {
        this.applicationVersion += smv;
      });
    this.$subscriptions.add(sysManVersionSub);
  }

  private subscribePerfManVersionEvents() {
    const perfManVersionSub = this.windowEvents.perfManVersion$
      .subscribe(pmv => {
        this.applicationVersion += pmv;
      });
    this.$subscriptions.add(perfManVersionSub);
  }

  private subscribeValEngineVersionEvents() {
    const valEngineVersionSub = this.windowEvents.validationEngineVersion$
      .subscribe(vev => {
        this.applicationVersion += vev;
      });
    this.$subscriptions.add(valEngineVersionSub);
  }

  private subscribePubRepVersionEvents() {
    const pubRepVersionSub = this.windowEvents.publicReportVersion$
      .subscribe(prv => {
        this.applicationVersion += prv;
      });
    this.$subscriptions.add(pubRepVersionSub);
  }

  private subscribeToIFrameService() {
    // Indicator Factory Client
    this.$subscriptions.add(this.iframeService.indicatorSource$
      .subscribe(path => {
        this.logger.debug('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `indicator - postMessage new path: ${path}`, '');
        this.logger.debug('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `indicator - postMessage old path: ${this.indicatorFrameProp.srcString}`, '');
        const currentPath = this.indicatorFrameProp.srcString;
        const pageLoadRequired = this.isPageLoadRequired(path, currentPath);
        if (pageLoadRequired) {
          this.indicatorFrameProp.srcString = path;
          const indFrame = document.getElementById('appIndicatorIframe') as HTMLIFrameElement;
          if (indFrame) {
            this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `indicator - postMessage: ${path}`, '');
            this.validateIframeUrl('Indicator', path);
            const indContentWindow = this.safeAccessIframeContentWindow('Indicator', indFrame);
            if (indContentWindow) {
              indContentWindow.postMessage('ndnqiwrapper-indicatornavigation:' + path, '*');
            }
            if (environment.environment === 'Local') {
              // When running locally the token & subscription have to be passed to the subdomains in the iframe
              const tokenString = sessionStorage.getItem(environment.AccessTokenID);
              indFrame.contentWindow.postMessage('ndnqiwrapper-token:' + tokenString, '*');
              let subid = sessionStorage.getItem(environment.SubscriptionSelected);
              subid = (subid) ? subid : '';
              indFrame.contentWindow.postMessage('ndnqiwrapper-subscription:' + subid, '*');
            }
          }
        }
      })
    );
    this.$subscriptions.add(this.iframeService.indicatorVisibility$
      .subscribe(isVisible => {
        this.logger.debug('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `changing indicatorFrameProp visibility: ${isVisible}`, '');
        this.indicatorFrameProp.visibility = isVisible;
      })
    );
    // Portal Client
    this.$subscriptions.add(this.iframeService.portalSource$
      .subscribe(path => {
        this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `portal - postMessage new path: ${path}`, '');
        this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `portal - postMessage old path: ${this.portalFrameProp.srcString}`, '');
        // console.log("PORTAL IFRAME = " + path);
        this.validateIframeUrl('Portal', path);
        this.portalFrameProp.srcString = path;
        this.portalFrameProp.src = this.sanitizer.bypassSecurityTrustResourceUrl(path);
        this.setIframeClass(path);
      })
    );
    this.$subscriptions.add(this.iframeService.portalVisibility$
      .subscribe(isVisible => {
        this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `portal - setting visibility: ${isVisible}`, '');
        this.portalFrameProp.visibility = isVisible;
      })
    );
    // Validation Engine
    this.$subscriptions.add(this.iframeService.validationSource$
      .subscribe(path => {
        if (this.validationFrameProp.srcString !== path) {
          this.validationFrameProp.srcString = path;
          const valFrame = document.getElementById('appValidationIFrame') as HTMLIFrameElement;
          if (valFrame) {
            this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `validation - postMessage: ${path}`, '');
            this.validateIframeUrl('Validation', path);
            const valContentWindow = this.safeAccessIframeContentWindow('Validation', valFrame);
            if (valContentWindow) {
              valContentWindow.postMessage('ndnqiwrapper-validationnavigation:' + path, '*');
            }

            if (environment.environment === 'Local') {
              // When running locally the token & subscription have to be passed to the subdomains in the iframe
              const tokenString = sessionStorage.getItem(environment.AccessTokenID);
              valFrame.contentWindow.postMessage('ndnqiwrapper-token:' + tokenString, '*');

              let subid = sessionStorage.getItem(environment.SubscriptionSelected);
              subid = (subid) ? subid : '';
              valFrame.contentWindow.postMessage('ndnqiwrapper-subscription:' + subid, '*');
            }
          }
        }
      })
    );
    this.$subscriptions.add(this.iframeService.validationVisibility$
      .subscribe(isVisible => {
        this.validationFrameProp.visibility = isVisible;
      })
    );
    // Performance Reports
    this.$subscriptions.add(this.iframeService.performanceSource$
      .subscribe(path => {
        if (this.performanceFrameProp.srcString !== path) {
          this.performanceFrameProp.srcString = path;
          const indFrame = document.getElementById('appPerformanceIFrame') as HTMLIFrameElement;
          if (indFrame) {
            this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `performancereport - postMessage: ${path}`, '');
            this.validateIframeUrl('Performance', path);
            const perfContentWindow = this.safeAccessIframeContentWindow('Performance', indFrame);
            if (perfContentWindow) {
              perfContentWindow.postMessage('ndnqiwrapper-performancenavigation:' + path, '*');
            }
          }
        }
      })
    );
    this.$subscriptions.add(this.iframeService.performanceVisibility$
      .subscribe(isVisible => {
        this.performanceFrameProp.visibility = isVisible;
      })
    );
    // System Management
    this.$subscriptions.add(this.iframeService.sysMngSource$
      .subscribe(path => {
        if (this.sysMngFrameProp.srcString !== path) {
          this.sysMngFrameProp.srcString = path;
          const indFrame = document.getElementById('appSystemManagementIFrame') as HTMLIFrameElement;
          if (indFrame) {
            this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `systemManagement - postMessage: ${path}`, '');
            this.validateIframeUrl('SystemManagement', path);
            const sysMngContentWindow = this.safeAccessIframeContentWindow('SystemManagement', indFrame);
            if (sysMngContentWindow) {
              sysMngContentWindow.postMessage('ndnqiwrapper-sysMngnavigation:' + path, '*');
            }
          }
        }
      })
    );
    this.$subscriptions.add(this.iframeService.sysMngVisibility$
      .subscribe(isVisible => {
        this.sysMngFrameProp.visibility = isVisible;
      })
    );
    // NDNQI Public Reports
    this.$subscriptions.add(this.iframeService.ndnqiPublicReportsSource$
      .subscribe(path => {
        if (this.ndnqiPublicReportsFrameProp.srcString !== path) {
          this.ndnqiPublicReportsFrameProp.srcString = path;
          const pubFrame = document.getElementById('appNdnqiPublicReportsIFrame') as HTMLIFrameElement;
          if (pubFrame) {
            this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `ndnqiPublicReports - postMessage: ${path}`, '');
            this.validateIframeUrl('PublicReports', path);
            const pubContentWindow = this.safeAccessIframeContentWindow('PublicReports', pubFrame);
            if (pubContentWindow) {
              pubContentWindow.postMessage('ndnqiwrapper-ndnqipublicreportsnavigation:' + path, '*');
            }
          }
        }
      })
    );
    this.$subscriptions.add(this.iframeService.ndnqiPublicReportsVisibility$
      .subscribe(isVisible => {
        this.ndnqiPublicReportsFrameProp.visibility = isVisible;
      })
    );
    //NDNQI Foundation: Unit Management
    this.$subscriptions.add(this.iframeService.ndnqiFoundationSource$
      .subscribe(path => {
        if (this.ndnqiFoundationFrameProp.srcString !== path) {
          this.ndnqiFoundationFrameProp.srcString = path;
          const foundationFrame = document.getElementById('appNdnqiFoundationIFrame') as HTMLIFrameElement;
          if (foundationFrame) {
            this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `ndnqiFoundation - postMessage: ${path}`, '');
            this.validateIframeUrl('NdnqiFoundation', path);
            const qpContentWindow = this.safeAccessIframeContentWindow('NdnqiFoundation', foundationFrame);
            if (qpContentWindow) {
              qpContentWindow.postMessage('ndnqiwrapper-foundationnavigation:' + path, '*');
            }
            if (environment.environment === 'Local') {
              const tokenString = sessionStorage.getItem(environment.AccessTokenID);
              foundationFrame.contentWindow.postMessage('ndnqiwrapper-token:' + tokenString, '*');
              let subid = sessionStorage.getItem(environment.SubscriptionSelected);
              subid = (subid) ? subid : '';
              foundationFrame.contentWindow.postMessage('ndnqiwrapper-subscription:' + subid, '*');
            }
          }
        }
      })
    );
    this.$subscriptions.add(this.iframeService.ndnqiFoundationVisibility$
      .subscribe(isVisible => {
        this.ndnqiFoundationFrameProp.visibility = isVisible;
      })
    );
    if (this.isDevOrLocalEnv) {
      // Learning Management
      this.$subscriptions.add(this.iframeService.learningMngSource$
        .subscribe(path => {
          if (this.learningMngFrameProp.srcString !== path) {
            this.learningMngFrameProp.srcString = path;
            const indFrame = document.getElementById('appLearningManagementIFrame') as HTMLIFrameElement;
            if (indFrame) {
              this.logger.info('app.component', 'subscribeToIFrameService', this.subscriptionSelected, this.subscriptions, `learningManagement - postMessage: ${path}`, '');
              this.validateIframeUrl('LearningManagement', path);
              const learnContentWindow = this.safeAccessIframeContentWindow('LearningManagement', indFrame);
              if (learnContentWindow) {
                learnContentWindow.postMessage('ndnqiwrapper-learningMngnavigation:' + path, '*');
              }
            }
          }
        })
      );
      this.$subscriptions.add(this.iframeService.learningMngVisibility$
        .subscribe(isVisible => {
          this.learningMngFrameProp.visibility = isVisible;
        })
      );
    }
  }

  private setPendo() {
    if (environment.production) {
      let iframe = document.getElementById('appPortalIframe') as HTMLIFrameElement;
      if (iframe && iframe.contentDocument) {
        this.setPendoVariables(iframe);
      }
      iframe = document.getElementById('appIndicatorIframe') as HTMLIFrameElement;
      if (iframe && iframe.contentDocument) {
        this.setPendoVariables(iframe);
      }
      const w: any = window;
      this.pendoService.checkPendo(w);
    }
  }

  private setPendoVariables(iframe: HTMLIFrameElement) {
    const userElement = iframe.contentDocument.getElementById('username');
    const visitor = (userElement) ? userElement.innerHTML : this.user;
    this.sessionService.setPendoUserId(visitor);
  }

  // Applications that use <iframe> may experience issues with sameSite=Lax or sameSite=Strict cookies because <iframe> is treated
  // as cross-site scenarios. SameSite=None must be used to allow cross-site cookie use. But - the value SameSite=None is not allowed
  // by the 2016 standard and causes some implementations to treat such cookies as SameSite=Strict.
  // These ASP.NET Session cookie issues cause issues when trying to maintain the Portal session across iframes.
  private subscribeToUserLoginStatusUpdates() {
    const monitoringSubscription = this.authService.userLoggedIn$
      .subscribe(isLoggedIn => {
        this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `Received login status update. Login=${isLoggedIn}`, '');
        this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `Received login status update. Already logged in isUserLoggedIn=${this.isUserLoggedIn}`, '');
        const menuType = this.navigationService.DisplayedMenuType;
        const menu = (menuType) ? menuType.toString() : '';
        this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `menuType=${menu}`, '');
        this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `settingMenuItems=${this.settingMenuItems}`, '');
        const isMenuRequired = (this.settingMenuItems === false) && (menu === '');
        this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `isMenuRequired=${isMenuRequired}`, '');
        // User already logged in
        if ((isLoggedIn === true) && (isLoggedIn === this.isUserLoggedIn)) {
           this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `User is already logged in`, '');
           // Reset the timeout timer if the token update was successful
          if (this.timeoutService.ResetTimeoutDialogTimer === true) {
            this.resetTimeoutDialogTimer();
          }
           // Handle user response to the Timeout Dialog
           if (this.IsTimeoutDialogVisible === true) {
              const permId = NavigationUtils.GetPermIdFromUrl(this._priorToTimeoutUrl);
              setTimeout(() => {
                if (permId && permId !== '') {
                  const ni = this.navigationService.DisplayedNavigationMenu.getNavigationItemUsingPermID(permId);
                  // console.log("User is logged in. Route to same domain");
                  this.routeToSameDomain(ni);
                } else {
                  // console.log("User is logged in. Set All IframeFrames");
                  this.iframeService.setAllIFrames(this._priorToTimeoutVisibility, this._priorToTimeoutUrl);
                }
                this.IsTimeoutDialogVisible = false;
              }, 0);
           } else {
              // Handle regular Token updates
              // console.log("Portal iframe visibility: " + this.portalFrameProp.visibility);
              if (this.portalFrameProp.visibility === false) {
                this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `Refreshing Portal page`, '');
                // console.log("Refreshing PortalSession page");
                this.iframeService.setNonVisiblePortalFrame();
              }
           }
        }
        if ((isLoggedIn === true) && (isLoggedIn !== this.isUserLoggedIn)) {
          const user = this.authService.getUser();
          this.user = user;
          this.logger.info('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, 'User logged in', '');
          const browser = this.getBrowserInfo();
          this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, `${browser}`, '');

          this.sessionService.clearHospitalSessionData();
          this.sessionService.clearUnitSessionData();
          this.logger.debug('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, 'Cleared Facility and Unit session data', '');
        }
        if ((isLoggedIn === true) && (isMenuRequired === true)) {
          this.logger.info('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, 'Setting Menu', '');
          this.settingMenuItems = true;
          if (isLoggedIn === true) {
             // Immediately fetch GlobalNav user info so isGlobalNavHxUser is available ASAP
           // this._gnFetchedOnce = false; // ensure guard allows a fetch after a new login
            this.subscribeGlobalNavUserInfo();
            this.resetTimeoutDialogTimer();
            this.subscribeToTimerCountdown();
            this.checkUserSubscriptions();
            this.loadUserNavigationMenusBySubscription();
            this.loadFederationData();
            this.setPendo();
          }
        }

        if (isLoggedIn === false) {
          this.logger.info('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, 'User logged out', '');
          this.globalHeaderMenu.clearGlobalNavUser(); 
         // this._gnFetchedOnce = false; // allow a fresh fetch next login within the same app session
          this.sessionService.clearSessionStorage();
          this.user = '';
          this.clearMenu();
          this.routeToLoggedOut();
          this.logger.info('app.component', 'subscribeToUserLoginStatusUpdates', this.subscriptionSelected, this.subscriptions, 'Completed user log out', '');
        }
        this.isUserLoggedIn = isLoggedIn;
      });
    this.$subscriptions.add(monitoringSubscription);
  }

  private checkUserSubscriptions() {
    this.subscriptions = this.sessionService.getSubscriptionsAvailable();
    let available = new Array<string>();
    if (this.subscriptions) {
      available = this.subscriptions.split(',');
    }
    this.availableSubscriptions.push(...available);
    this.isMultiSubscriptionUser = (available.length > 1);
  }

  private loadFederationData() {
    // Get the Federation Token
    if ((environment.EnableSingleSignOn) || (environment.EnableHigherLogic)) {
      if (environment.EnableHigherLogic) {
        this.subscribeToFederationTokenWithData();
      } else {
        this.subscribeToFederationToken();
      }
    }
  }

  private getBrowserInfo(): any {
    const deviceInfo = this.deviceService.getDeviceInfo();
    const browserDetails = `BROWSER:${deviceInfo.browser}, VERSION:${deviceInfo.browser_version}`;
    return browserDetails;
  }

  private subscribeToTimerCountdown() {
    if (environment.TimeoutCounterDisplayEnabled) {
      const timerSubscription = this.timeoutService.timeoutCountdown$
        .subscribe(
          (remainingTime) => {
            // console.log('TIME REMAINING: ', remainingTime);
            this.timeoutCounter = remainingTime;
          }
        );
        this.$subscriptions.add(timerSubscription);
    }
  }

  // Listen for connection changes
  private subscribeToTimeoutDecisionRequired() {
      const decisionRequiredSubscription = this.timeoutService.timeoutDecisionRequiredEvent
        .subscribe((show) => {
          if (show === true) {
            this.showTimeoutDialog();
          }
        });
      this.$subscriptions.add(decisionRequiredSubscription);
  }

  private subscribeToTimeoutDecisionExpired() {
    const decisionExpiredSubscription = this.timeoutService.timeoutDecisionExpiredEvent
      .subscribe((exp) => {
        if (exp === true) {
          // console.log("Timeout Dialog decision expired");
          this.confirmationService.close();
          this.timeoutLogOut();
        }
      });
    this.$subscriptions.add(decisionExpiredSubscription);
  }

  private resetTimeoutDialogTimer() {
    // console.log("Resetting Timeout Dialog timer");
    this.timeoutService.resetTimeoutDialogTimer();
    this.subscribeToTimerCountdown();
  }

  private setPriorToTimeoutDetails() {
    if(this.portalFrameProp && this.portalFrameProp.visibility) {
      this._priorToTimeoutVisibility = SubDomainEnum.Portal;
      this._priorToTimeoutUrl = this.portalFrameProp.srcString;
    }
    if(this.indicatorFrameProp && this.indicatorFrameProp.visibility) {
      this._priorToTimeoutVisibility = SubDomainEnum.IndicatorFactory;
      this._priorToTimeoutUrl = this.indicatorFrameProp.srcString;
    }
    if(this.performanceFrameProp && this.performanceFrameProp.visibility) {
      this._priorToTimeoutVisibility = SubDomainEnum.PerformanceReports;
      this._priorToTimeoutUrl = this.performanceFrameProp.srcString;
    }
    if(this.sysMngFrameProp && this.sysMngFrameProp.visibility) {
      this._priorToTimeoutVisibility = SubDomainEnum.SystemManagement;
      this._priorToTimeoutUrl = this.sysMngFrameProp.srcString;
    }
    if(this.validationFrameProp && this.validationFrameProp.visibility) {
      this._priorToTimeoutVisibility = SubDomainEnum.ValidationEngine;
      this._priorToTimeoutUrl = this.validationFrameProp.srcString;
    }
    if(this.ndnqiPublicReportsFrameProp && this.ndnqiPublicReportsFrameProp.visibility) {
      this._priorToTimeoutVisibility = SubDomainEnum.NdnqiPublicReports;
      this._priorToTimeoutUrl = this.ndnqiPublicReportsFrameProp.srcString;
    }
    if(this.learningMngFrameProp && this.learningMngFrameProp.visibility) {
      this._priorToTimeoutVisibility = SubDomainEnum.LearningManagement;
      this._priorToTimeoutUrl = this.learningMngFrameProp.srcString;
    }
  }

  private showTimeoutDialog() {
    if (this.isUserLoggedIn === true) {
      this.logger.info('app.component', 'showTimeoutDialog', this.subscriptionSelected, this.subscriptions, 'Showing Timeout Dialog', '');
      setTimeout(() => {
        // console.log("Showing Timeout Dialog");
        this.setPriorToTimeoutDetails();
        // console.log("Displaying Timeout Dialog");
        this.IsTimeoutDialogVisible = true;
        const portalBlankRoute = this.iframeService.getSessionPath();
        const urlParts = portalBlankRoute.split('?');
        const path = urlParts[0];
        const navExtras = this.mapQueryParams(urlParts);
        // console.log("Displaying Portal Session Page");
        this.navigateToSameDomain(path, navExtras);
      }, 0);
    }
  }

  private displayTimeoutDialog() {
    this.confirmationService.confirm({
      key: 'timeoutConfirmation',
      message: `Your session is expiring in ${environment.TimeoutDecisionTime} seconds.<p></p>Do you want to stay logged in ?`,
      header: 'Session Expiration',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Stay Logged In',
      rejectLabel: 'Log Out',
      accept: () => {
        this.timeoutStayLoggedIn();
      },
      reject: () => {
        this.timeoutLogOut();
      }
    });
  }

  private timeoutStayLoggedIn() {
    this.logger.info('app.component', 'timeoutStayLoggedIn', this.subscriptionSelected, this.subscriptions, 'User elected to stay logged in', '');
    this.timeoutService.ResetTimeoutDialogTimer = false;
    this.timeoutService.timeoutStayLoggedIn();
    // Logic to stay logged in. Get new token.
    this.requestFreshToken();
  }

  private timeoutLogOut() {
    this.logger.info('app.component', 'timeoutLogOut', this.subscriptionSelected, this.subscriptions, 'User elected to log out', '');
    this.logUserOut();
  }

  private loadHospitalListMenu() {
    const userNav = this.navigationService.DisplayedNavigationMenu;
    const menuItems = this.getMenuItems(userNav, NavigationMenuTypeEnum.HospitalLevel, true);
    this.navigationService.HospitalLevelMenuItemCache = menuItems;
    this.setDisplayMenu(userNav, NavigationMenuTypeEnum.HospitalLevel, environment.HomePermId, true);
    this.breadcrumbService.setHomeBreadcrumb();
  }

  private loadSingleHospitalMenu() {
    const userNav = this.navigationService.SingleHospitalUserNavigationCache;
    // Clone separately
    const clonedUserNav = this.navigationService.CloneUserNavigation(userNav);
    const navItem = userNav.getNavigationItemUsingPermID(this.activeLinkId);
    const menuItems = this.getMenuItems(clonedUserNav, NavigationMenuTypeEnum.SingleHospital, false);
    this.navigationService.SingleHospitalMenuItemCache = menuItems;
    const itemsDisplayed = this.items;
    if (itemsDisplayed) {
      // Load menu into slide control and then navigate
      const hospMenuItemsMap = this.navigationService.HospitalMenuItemCache;
      const unitMenuItemsMap = this.navigationService.UnitMenuItemCache;
      const menuType = this.navigationService.DisplayedMenuType;
      const truncatedMenuItems = this.navigationMappingService.getTruncatedMenuItems(menuType, clonedUserNav, this.activeLinkId, hospMenuItemsMap, unitMenuItemsMap);
      this.items = this.navigationService.loadOnlyRequiredHospitalsChildItemsFromCache(navItem, truncatedMenuItems, this.items, this.subscriptionSelectedPermId, unitMenuItemsMap);
      this.clickMenuItem(navItem.PermItemID);
      this.navigateToItem(navItem);
    } else {
      // Set up slide control and then navigate
      const truncatedUserNav = this.navigationMappingService.truncateNavMenu(clonedUserNav, this.activeLinkId);
      this.breadcrumbService.setHomeBreadcrumb();
      const goHome = ((this.activeLinkId) && (environment.HomePermId === this.activeLinkId));
      this.setDisplayMenu(truncatedUserNav, NavigationMenuTypeEnum.SingleHospital, this.activeLinkId, goHome);
    }
  }

  private loadFullMenu() {
    const userNav = this.navigationService.FullUserNavigationCache;
    const fullMenuItems = this.getMenuItems(userNav, NavigationMenuTypeEnum.FullMenu, false);
    this.navigationService.FullMenuItemCache = fullMenuItems;
    const itemsDisplayed = this.items;
    if (!itemsDisplayed) {
      const clonedUserNav = this.navigationService.CloneUserNavigation(userNav);
      const hospMenuItemsMap = this.navigationService.HospitalMenuItemCache;
      const unitMenuItemsMap = this.navigationService.UnitMenuItemCache;
      const truncatedMenuItems = this.navigationMappingService.getTruncatedMenuItems(NavigationMenuTypeEnum.FullMenu, clonedUserNav, this.activeLinkId, hospMenuItemsMap, unitMenuItemsMap);
      this.setDisplayMenuTruncated(clonedUserNav, truncatedMenuItems, this.activeLinkId, true);
      this.breadcrumbService.setHomeBreadcrumb();
    }
  }

  private loadMenuType(menuType: NavigationMenuTypeEnum) {
    if (this.isUserLoggedIn === true) {
      this.logger.info('app.component', 'loadMenuType', this.subscriptionSelected, this.subscriptions, `isUserLoggedIn=${this.isUserLoggedIn}`, '');
      const currentMenuDisplayed = this.navigationService.DisplayedMenuType;
      if ((!currentMenuDisplayed) || (menuType >= currentMenuDisplayed)) {
        this.navigationService.DisplayedMenuType = menuType;
        this.iframeService.setLinkId(environment.HomePermId);
        switch (menuType) {
          case NavigationMenuTypeEnum.HospitalLevel:
            this.logger.info('app.component', 'loadMenuType', this.subscriptionSelected, this.subscriptions, `loadHospitalListMenu`, '');
            this.loadHospitalListMenu();
            break;
          case NavigationMenuTypeEnum.SingleHospital:
            this.logger.info('app.component', 'loadMenuType', this.subscriptionSelected, this.subscriptions, `loadSingleHospitalMenu`, '');
            this.loadSingleHospitalMenu();
            break;
          case NavigationMenuTypeEnum.FullMenu:
            this.logger.info('app.component', 'loadMenuType', this.subscriptionSelected, this.subscriptions, `loadFullMenu`, '');
            this.loadFullMenu();
            break;
          default:
            break;
        }
      }
    }
    this.settingMenuItems = false;
  }

  private loadUserNavigationMenusBySubscription() {
    this.clearMenu();
    if (environment.TryFullMenu) {
      this.subscribeToMenuLoadWithFallback();
    } else {
      this.subscribeToHospitalListMenuLoad();
    }
  }

  private subscribeToHospitalListMenuLoad() {
    this.settingMenuItems = true;
    const navSubscription = this.navigationService.getHospitalListUserNavigation()
      .subscribe(result => {
        if (result.Ok === true && result.Status === 200) {
          const userNav = this.navigationMappingService.mapUserNavigation(result.UserNavigation);
          if (!this.activeLinkId) {
            this.activeLinkId = environment.HomePermId;
          }
          this.navigationService.UserNavigationHospitalLevel = userNav;
          this.navigationService.HospitalLevelUserNavigationCache = userNav;
          this.loadMenuType(NavigationMenuTypeEnum.HospitalLevel);
        } else if (result.Ok === false && result.StatusText.includes("401 Unauthorized")) {
          this.logUserOut();
        } else {
          this.menuLoadError = true;
        }
        this.settingMenuItems = false;
      });
    this.$subscriptions.add(navSubscription);
  }

  private loadUserNavigationSingleHospitalMenu(hospid: string) {
    this.subscribeToSingleHospitalMenuLoad(hospid);
  }

  private subscribeToMenuLoadWithFallback() {
    this.settingMenuItems = true;
    const navSubscription = this.navigationService.getUserNavigationWithFallback()
      .subscribe(result => {
        if (result.Ok === true && result.Status === 200) {
          const userNav = this.navigationMappingService.mapUserNavigation(result.UserNavigation);
          if (!this.activeLinkId) {
            this.activeLinkId = environment.HomePermId;
          }
          if (result.NavigationMenuLevel === NavigationMenuTypeEnum.FullMenu) {
            this.navigationService.UserNavigationFull = userNav;
            this.navigationService.FullUserNavigationCache = userNav;
            this.loadMenuType(NavigationMenuTypeEnum.FullMenu);
          } else {
            this.navigationService.UserNavigationHospitalLevel = userNav;
            this.navigationService.HospitalLevelUserNavigationCache = userNav;
            this.loadMenuType(NavigationMenuTypeEnum.HospitalLevel);
          }
        } else if (result.Ok === false && result.StatusText.includes("401 Unauthorized")) {
          this.logUserOut();
        } else {
          this.menuLoadError = true;
        }
        this.settingMenuItems = false;
      });
    this.$subscriptions.add(navSubscription);
  }

  private subscribeToSingleHospitalMenuLoad(hospid: string) {
    this.settingMenuItems = true;
    const navSubscription = this.navigationService.getSingleHospitalUserNavigation(hospid)
      .subscribe(nav => {
        const result: UserNavigationResult = nav;
        this.logger.info('app.component', 'subscribeToSingleHospitalMenuLoad', this.subscriptionSelected, this.subscriptions, `Received response. Status: ${result.Ok}`, '');
        if ((result.Ok === true) && (result.Status === 200)) {
          // map result
          this.logger.debug('app.component', 'subscribeToSingleHospitalMenuLoad', this.subscriptionSelected, this.subscriptions, 'Starting mapping', '');
          const userNav = this.navigationMappingService.mapUserNavigation(result.UserNavigation);
          if (!this.activeLinkId) {
            this.activeLinkId = environment.HomePermId;
          }
          this.logger.debug('app.component', 'subscribeToSingleHospitalMenuLoad', this.subscriptionSelected, this.subscriptions, 'Ended mapping', '');
          this.navigationService.UserNavigationSingleHospital = userNav;
          this.navigationService.SingleHospitalUserNavigationCache = userNav;
          this.logger.debug('app.component', 'subscribeToSingleHospitalMenuLoad', this.subscriptionSelected, this.subscriptions, `Single menu cached: ${hospid}`, '');
          this.loadMenuType(NavigationMenuTypeEnum.SingleHospital);
        } else if ((result.Ok === false) && (result.StatusText.includes("401 Unauthorized"))) {
          this.logger.warn('app.component', 'subscribeToSingleHospitalMenuLoad', this.subscriptionSelected, this.subscriptions, `Unauthorized access to Single Facility menu. Response Status: ${result.Status}`, '');
          this.logUserOut();
        } else {
          // display error ?
          this.menuLoadError = true;
        }
      });
    this.$subscriptions.add(navSubscription);
  }

  private subscribeToFederationTokenWithData() {
    const email = this.user;
    const ssoSubscription = this.federationService.getFederationToken()
      .pipe(
        concatMap(data => {
          const result: DataResult = data;
          this.logger.info('app.component', 'subscribeToFederationToken', this.subscriptionSelected, this.subscriptions, `Received response. Status: ${result.Ok}`, '');
          if (result.Ok === true) {
            this.authService.setFederationToken(result.Data);
            this.isFederationLoaded = true;
            return this.userRegistrationService.getUserCommunities(email)
          }
          const emptyResult = new DataResult(false, 500, 'Service Unavailable error', this._NO_DATA);
          return of(emptyResult);
        })
      )
      .subscribe(data => {
        if (data.Ok === true) {
          const reg = data.Data;
          this.addUserRegistrations(reg);
        }
      });
    this.$subscriptions.add(ssoSubscription);
  }

  private subscribeToFederationToken() {
    const ssoSubscription = this.federationService.getFederationToken()
      .subscribe(t => {
        const result: DataResult = t;
        this.logger.info('app.component', 'subscribeToFederationToken', this.subscriptionSelected, this.subscriptions, `Received response. Status: ${result.Ok}`, '');
        if (result.Ok === true) {
          this.authService.setFederationToken(result.Data);
          this.isFederationLoaded = true;
        }
      });
    this.$subscriptions.add(ssoSubscription);
  }

  private addUserRegistrations(identityProviders: any) {
    const usersCommunities = new Array<string>();
    identityProviders.registrations.forEach(idp => {
      if (idp.communities) {
        idp.communities.forEach(com => {
          if (usersCommunities.indexOf(com) < 0) {
            usersCommunities.push(com);
          }
        });
      }
    });
    const communities = usersCommunities.join(';');
    this.sessionService.setUserCommunities(communities);
  }

  private getHomeItem(): NavigationItem {
    const nav = this.navigationService.DisplayedNavigationMenu;
    return nav.Items[0];
  }

  private navigateHome() {
    const home = this.getHomeItem();
    this.handleRouterNavigation(home);
    const menu = this.slideMenu;
    const menuAnimated = menu.left < 0;
    while (menu.left < 0) {
      menu.goBack(new MouseEvent('click'));
    }
    if (menuAnimated) {
      menu.focusedItemInfo.set({
        index: -1,
        level: 0,
        parentKey: ''
      });
      const activeItemPath = menu.activeItemPath().filter((p) => p.parentKey !== '');
      menu.activeItemPath.set(activeItemPath);
    }
  }

  private subscribeToNavigationChanges() {
    const navigationCompletedSubscription = this.navigationService.navigationComplete$
      .subscribe(linkid => {
        this.logger.debug('app.component', 'subscribeToNavigationChanges', this.subscriptionSelected, this.subscriptions, `Navigation Complete. LinkId=${linkid}`, '');
        this.activeLinkId = linkid;
        this.backButtonClicked = false;
        this.iframeService.LoadingLinkId = '';
      });
    this.$subscriptions.add(navigationCompletedSubscription);
  }

  private subscribeToBreadcrumbEvents() {
    const crumbSelectedSubscription = this.breadcrumbService.crumbSelected$
      .subscribe(crumb => {
        this.logger.debug('app.component', 'subscribeToBreadcrumbEvents', this.subscriptionSelected, this.subscriptions, `Breadcrumb clicked. Crumb=${crumb.key}`, '');
        this.onCrumbClicked(crumb);
      });
    this.$subscriptions.add(crumbSelectedSubscription);
  }

  private clearMenu() {
    this.logger.debug('app.component', 'clearMenu', this.subscriptionSelected, this.subscriptions, 'Clearing menu and breadcrumbs', '');
    this.navigationService.clearMenus();
    this.breadcrumbService.clearBreadcrumbs();
    this.logger.debug('app.component', 'clearMenu', this.subscriptionSelected, this.subscriptions, `clear slidemenu host`, '');
    this.logger.debug('app.component', 'clearMenu', this.subscriptionSelected, this.subscriptions, 'slidemenu host cleared', '');
  }

  clearSelectedItem() {
    try {
      const menuDOM = this.slideMenu?.slideMenuContentViewChild?.nativeElement;
      if (!menuDOM) {
        this.logger.error('app.component', 'clearSelectedItem', this.subscriptionSelected, this.subscriptions,
          'MenuDOM is null - slideMenuContentViewChild.nativeElement not available', 
          `slideMenu exists: ${!!this.slideMenu}, slideMenuContentViewChild exists: ${!!this.slideMenu?.slideMenuContentViewChild}`);
        return;
      }
      
      const selected = menuDOM.querySelector('.p-active-submenu .p-menuitem-active');
      if (selected) {
        selected.classList.remove('p-menuitem-active');
      }
    } catch (error) {
      this.logger.error('app.component', 'clearSelectedItem', this.subscriptionSelected, this.subscriptions,
        'Error clearing selected menu item', `Error: ${error}`);
    }
  }

  handleBackButtonEvent($event) {
    const penultimate = this.breadcrumbService.getPenultimate();
    if (penultimate) {
      this.backButtonClicked = true;
      this.clearSelectedItem();
      const navItem = this.navigationService.CachedNavigationMenu.getNavigationItemUsingPermID(penultimate.Item.key);
      this.finalDestination = (navItem && navItem.Path) ? navItem.Path.Directory : '';
      const isSubLevel = NavigationUtils.IsLastPartSubscriptionItemFromPermId(navItem.PermItemID);
      const isHome = navItem.PermItemID === environment.HomePermId;
      if (isSubLevel && !isHome) {
        const menuitem = this.navigationService.getMenuItem(penultimate.Item.key, this.items);
        let childStubExists = (menuitem && menuitem.items && NavigationUtils.IsStubFromPermId(menuitem.items[0].id));
        if (childStubExists) {
          this.finalDestination = (navItem && navItem.Path) ? navItem.Path.Directory : '';
        } else {
          this.finalDestination = 'if/blankpage';
        }
      }
      this.executeMenuItemCommand(penultimate.Item.key, $event);
    }
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Clicks through the whole hierarchy down to the link
  private async setMenuPosition(userNavigation: UserNavigation, activeLinkId: string, navigateHierarchy: boolean) {
    let hierarchy: Array<NavigationItem>;
    if (activeLinkId && (activeLinkId.length > 0)) {
      hierarchy = userNavigation.getNavigationItemHierarchyUsingPermID(this.activeLinkId);
    }
    if (hierarchy && (hierarchy.length > 0)) {
      this.logger.debug('app.component', 'setMenuPosition', this.subscriptionSelected, this.subscriptions, `Menu and Breadcrumb need to change. Bookmarked/emailed link have been used. LinkId:${this.activeLinkId}`, '');
      this.settingMenuPositioning = true;
      // log Hierarchy Path
      let path = '';
      let lastNavItem;
      hierarchy.forEach(ni => {
        path = `${path} -> ${ni.ItemID}`;
      });
      this.logger.debug('app.component', 'setMenuPosition', this.subscriptionSelected, this.subscriptions, `Menu Position:${path}`, '');
      // click Through Menu
      this.slideMenu.effectDuration = 0;
      if (navigateHierarchy === true) {
        for (let index = hierarchy.length - 1; index >= 0; index--) {
          const navitem = hierarchy[index];
          await this.delay(50);
          this.clickMenuItem(navitem.PermItemID);
        }
        lastNavItem = hierarchy[0];
      } else {
        lastNavItem = hierarchy.shift();
        await this.delay(50);
        this.clickMenuItem(lastNavItem.PermItemID);
      }
      this.settingMenuPositioning = false;
      this.slideMenu.effectDuration = 250;
      if (lastNavItem) {
        this.navigateToItem(lastNavItem);
      }
    }
  }

  private async clickThroughMenu(userNavigation: UserNavigation, oldActiveLinkId: string, newActiveLinkId: string) {
    this.settingMenuPositioning = true;
    this.slideMenu.effectDuration = 0;
    let newHierarchy: Array<NavigationItem>;
    let oldHierarchy: Array<NavigationItem>;
    if (newActiveLinkId && newActiveLinkId.length) {
      newHierarchy = userNavigation.getNavigationItemHierarchyUsingPermID(newActiveLinkId);
      newHierarchy = newHierarchy.reverse();
    }
    if (oldActiveLinkId && oldActiveLinkId.length) {
      oldHierarchy = userNavigation.getNavigationItemHierarchyUsingPermID(oldActiveLinkId);
      oldHierarchy = oldHierarchy.reverse();
    }
    if (newHierarchy && newHierarchy.length) {
      const stepsBack = this.calculateStepsBack(oldHierarchy, newHierarchy);
      this.slideMenu.effectDuration = 0;
      this.clickMenuBackward(stepsBack);
      const startIndex = oldHierarchy.length - stepsBack;
      const {
        navigationService,
        navigationMappingService,
        subscriptionSelectedPermId
      } = this;
      for (let index = startIndex; index < newHierarchy.length; ++index) {
        const navitem = newHierarchy[index];
        const isUnitItem = NavigationUtils.IsUnitLevel(navitem.PermItemID);
        if (isUnitItem) {
          const { items } = this;
          const hospMenuItemsMap = navigationService.HospitalMenuItemCache;
          const unitMenuItemsMap = navigationService.UnitMenuItemCache;
          const menuType = navigationService.DisplayedMenuType;
          const clonedUserNav = navigationService.CloneUserNavigation(userNavigation);
          const truncatedMenuItems = navigationMappingService.getTruncatedMenuItems(menuType, clonedUserNav, navitem.PermItemID, hospMenuItemsMap, unitMenuItemsMap);
          this.items = navigationService.loadOnlyRequiredHospitalsChildItemsFromCache(navitem, truncatedMenuItems, items, subscriptionSelectedPermId, unitMenuItemsMap);
          this.navigationService.updateDisplayedNavigationMenu(clonedUserNav);
        }
        this.clickMenuItem(navitem.PermItemID);
      }
    }
    await this.delay(50);
    this.settingMenuPositioning = false;
  }

  private calculateStepsBack(oldHierarchy: Array<NavigationItem>, newHierarchy: Array<NavigationItem>): number {
    if (!oldHierarchy || !newHierarchy) {
      return 0;
    }
    const minLen = Math.min(oldHierarchy.length, newHierarchy.length);
    let commonDepth = 0;
    for (; commonDepth < minLen; ++commonDepth) {
      if (oldHierarchy[commonDepth].PermItemID !== newHierarchy[commonDepth].PermItemID) {
        break;
      }
    }
    // Steps back are the levels below (and including) the divergent node in the *old* path
    const stepsBack = oldHierarchy.length - commonDepth;
    return stepsBack;
  }

   private deepSearch(itemId: string, searchArray: any[], allowPartialMatch: boolean, found?: any) {
    searchArray.find(item => {
      const escapedItemId = itemId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regexPattern = allowPartialMatch ? `^${escapedItemId}[\-_][^\-_]+$` : `^${escapedItemId}$`;
      const regex = new RegExp(regexPattern);
      if (regex.test(item.item.id)) {
        found = item;
      } else if (item?.items?.length) {
        found = this.deepSearch(itemId, item.items, allowPartialMatch, found);
      }
    });
    return found;
  }

  private clickMenuItem(itemId: string) {
    const intervalID = setInterval(id => {
        if (!this.menuLoading) {
          const menu = this.slideMenu;
          const { processedItems } = menu;
          let processedItem = this.deepSearch(id, processedItems, false);
          if (processedItem === undefined) {
            // part of a group, get the parent
            let parts = id.split('_');
            let base = `${parts[0]}_${parts[1]}`;
            parts.splice(0, 2);
            if (parts.length === 0 || (parts.length === 1 && parts[0].startsWith('f'))) {
              processedItem = this.deepSearch(base, processedItems, true);
            } else if (parts.length > 1) {
              while (parts.length > 0 && !processedItem) {
                for (let i = 0; i < parts.length; ++i) {
                  base = `${base}_${parts[i]}`;
                }
                processedItem = this.deepSearch(base, processedItems, true);
                parts.pop();
              }
            }
          }
          if (processedItem !== undefined) {
            const event = new PointerEvent('click', {
              bubbles: true,
              cancelable: true,
              composed: true,
              pointerId: 1,
              pointerType: 'mouse',
              isPrimary: true
            });
            menu.onItemChange({ originalEvent: event, processedItem });
            this.subMenu.itemClick.emit({ originalEvent: event, processedItem, isFocus: true });
          }
          menu.effectDuration = 250;
          clearInterval(intervalID)
        }
    }, 100, itemId);
  }

  private getMenuItems(userNavigation: UserNavigation, menuType: NavigationMenuTypeEnum, includeStubs: boolean = true): MenuItem[] {
    const hospMenuItemCache = this.navigationService.HospitalMenuItemCache;
    const unitMenuItemCache = this.navigationService.UnitMenuItemCache;
    const useExtensionMenu = environment.UseMenuExtension;
    const addStubs = includeStubs && useExtensionMenu;
    return this.navigationMappingService.getMenuItems(menuType, userNavigation, this.activeLinkId, addStubs, hospMenuItemCache, unitMenuItemCache);
  }

  private setDisplayMenu(userNavigation: UserNavigation, menuType: NavigationMenuTypeEnum, activeLinkId: string, goHome: boolean) {
    this.logger.debug('app.component', 'setDisplayMenu', this.subscriptionSelected, this.subscriptions, 'converting UserNavigation to MenuItems', '');
    if (this.authService.isUserLoggedIn() === true) {
      const menuItems = this.getMenuItems(userNavigation, menuType);
      this.logger.info('app.component', 'setDisplayMenu', this.subscriptionSelected, this.subscriptions, `converted MenuItems: ${menuItems.length}`, '');
      this.navigationService.setNavigationState(menuType, menuItems, userNavigation);
      this.items = menuItems;
      this.logger.info('app.component', 'setDisplayMenu', this.subscriptionSelected, this.subscriptions, `MenuItems: ${this.items.length}`, '');
      if (goHome === true) {
        this.logger.info('app.component', 'setDisplayMenu', this.subscriptionSelected, this.subscriptions, 'navigate Home', '');
        this.navigateHome();
      } else if (activeLinkId && activeLinkId.length > 0) {
        this.logger.info('app.component', 'setDisplayMenu', this.subscriptionSelected, this.subscriptions, `set menu position: ${activeLinkId}`, '');
        this.setMenuPosition(userNavigation, activeLinkId, true);
      }
      this.settingMenuItems = false;
    }
  }

  private setDisplayMenuTruncated(userNavigation: UserNavigation, truncatedMenuItems: MenuItem[], activeLinkId: string, goHome: boolean) {
    if (this.authService.isUserLoggedIn() === true) {
      this.items = truncatedMenuItems;
      this.logger.info('app.component', 'setDisplayMenuTruncated', this.subscriptionSelected, this.subscriptions, `MenuItems: ${this.items.length}`, '');
      if (goHome === true) {
        this.navigateHome();
      } else if (activeLinkId && activeLinkId.length > 0) {
        this.setMenuPosition(userNavigation, activeLinkId, true);
      }
      this.settingMenuItems = false;
    }
  }

  private routeToPirResolver(navItem: NavigationItem, navigationExtras: any) {
    const path = navItem.Path.toRelativeString();
    const routerPath = `if${path}`;
    const permId = (navItem.PermItemID) ? navItem.PermItemID : '';
    const params = navItem.Path.toQueryParam();
    params.linkid = permId;
    this.handleIndicatorResolverNavigation(routerPath, navigationExtras, navItem);
  }

  private routeToSameDomain(navItem: NavigationItem) {
    const path = navItem.Path.toRelativeString();
    let routerPath = path;
    if (!path.includes(`if/`)) {
      routerPath = `if${path}`;
    }
    const permId = (navItem.PermItemID) ? navItem.PermItemID : '';
    const params = navItem.Path.toQueryParam();
    params.linkid = permId;
    const navigationExtras = {
      queryParams: params
    };
    const isIndicatorResolver = environment.EnableIndicatorResolver && (routerPath.startsWith(environment.IndicatorResolverPath));
    const isPirResolver = environment.EnableIndicatorResolver && (routerPath.startsWith(environment.PirResolverPath));
    const isDashboard = (routerPath === environment.DashboardPath);
    const isHospitalLevel = NavigationUtils.IsHospitalLevel(permId);
    if (this.backButtonClicked) {
      const isDestination = (routerPath.includes(this.finalDestination));
      if (isDestination) {
        this.navigateToSameDomain(routerPath, navigationExtras);
      } else if (isIndicatorResolver) {
        this.handleIndicatorResolverNavigation(routerPath, navigationExtras, navItem);
      } else if (isPirResolver) {
        this.handlePirResolverNavigation(routerPath, navigationExtras, navItem);
      }
    } else if (isIndicatorResolver) {
      this.handleIndicatorResolverNavigation(routerPath, navigationExtras, navItem);
    }
    else if (isPirResolver) {
      this.handlePirResolverNavigation(routerPath, navigationExtras, navItem);
    }
    else if (isDashboard && isHospitalLevel) {
      const hospid = NavigationUtils.GetHospitalIdFromPermId(permId);
      this.handleHospitalDashboardNavigation(hospid, routerPath, navigationExtras);
    }
    else {
      this.navigateToSameDomain(routerPath, navigationExtras);
    }
  }

  private handleIndicatorResolverNavigation(routerPath: string, navigationExtras: any, navItem: NavigationItem) {
    this.settingMenuItems = true;
    const params = navigationExtras.queryParams;
    const indid = Number(params.indid);
    const resolverSubscription = this.indicatorResolverService.getGetIndicatorFullRoute(this.user, navItem, navigationExtras, indid)
      .subscribe(routeResult => {
        const result: DataResult = routeResult;
        this.logger.info('app.component', 'handleIndicatorResolverNavigation', this.subscriptionSelected, this.subscriptions, `Indicator routing response. Status: ${result.Status}`, '');
        if ((result.Ok === true) && (result.Status === 200)) {
          const np: NavigationItem = result.Data;
          const resolvedPath = np.Path.toRelativeString();
          const resolvedRouterPath = `if${resolvedPath}`;
          const params = np.Path.toQueryParam();
          params.month = navigationExtras.queryParams.month;
          const permId = (np.PermItemID) ? np.PermItemID : '';
          params.linkid = permId;
          const resolvedNavExtras = {
            queryParams: params
          };
          this.iframeService.LoadingLinkId = permId;
          this.navigateToSameDomain(resolvedRouterPath, resolvedNavExtras);
          // Sync updated child items to menu items and reset their callbacks
          const indicatorMenuItem = this.navigationService.getMenuItem(navItem.PermItemID, this.items);
          if (indicatorMenuItem && indicatorMenuItem.items && navItem.Items) {
            const callback = this.navigationMappingService.MenuItemCallback;
            navItem.Items.forEach(srcItem => {
              const destMenuItem = indicatorMenuItem.items.find((mi: MenuItem) => mi.label === srcItem.ItemName);
              if (destMenuItem) {
                destMenuItem.id = srcItem.PermItemID;
                destMenuItem.command = callback;
              }
            });
          }
        } else if ((result.Ok === true) && (result.Status === 401)) {
          this.logger.warn('app.component', 'handleIndicatorResolverNavigation', this.subscriptionSelected, this.subscriptions, `Indicator routing was not successful. Response Status: ${result.Status}`, '');
          this.logUserOut();
        } else {
          this.logger.warn('app.component', 'handleIndicatorResolverNavigation', this.subscriptionSelected, this.subscriptions, `Indicator routing was not successful. Response Status: ${result.Status}`, result.StatusText);
          routerPath = 'if/routingerror';
          this.navigateToSameDomain(routerPath, navigationExtras);
        }
        this.settingMenuItems = false;
      });
    this.$subscriptions.add(resolverSubscription);
  }

  private handlePirResolverNavigation(routerPath: string, navigationExtras: any, navItem: NavigationItem) {
    this.settingMenuItems = true;
    const indid = (navItem.ItemName.startsWith('P')) ? environment.PressureInjuriesIndicatorId : environment.RestraintsIndicatorId;
    const resolverSubscription = this.indicatorResolverService.getGetIndicatorFullRoute(this.user, navItem, navigationExtras, indid)
      .subscribe(routeResult => {
        const result: DataResult = routeResult;
        this.logger.info('app.component', 'handlePirResolverNavigation', this.subscriptionSelected, this.subscriptions, `Indicator routing response. Status: ${result.Status}`, '');
        if ((result.Ok === true) && (result.Status === 200)) {
          const np: NavigationItem = result.Data;
          const permId = (np.PermItemID) ? np.PermItemID : '';
          this.iframeService.LoadingLinkId = permId;
          this.navigateToSameDomain(routerPath, navigationExtras);
        } else if ((result.Ok === true) && (result.Status === 401)) {
          this.logger.warn('app.component', 'handlePirResolverNavigation', this.subscriptionSelected, this.subscriptions, `PIR routing was not successful. Response Status: ${result.Status}`, '');
          this.logUserOut();
        } else {
          this.logger.warn('app.component', 'handlePirResolverNavigation', this.subscriptionSelected, this.subscriptions, `PIR routing was not successful. Response Status: ${result.Status}`, result.StatusText);
          routerPath = 'if/routingerror';
          this.navigateToSameDomain(routerPath, navigationExtras);
        }
        this.settingMenuItems = false;
      });
    this.$subscriptions.add(resolverSubscription);
  }

  // Call Hospital Change Service. If a successful response is received, navigate to the dashboard
  private handleHospitalDashboardNavigation(hospid: string, routerPath: string, navigationExtras: any) {
    this.settingMenuItems = true;
    this.hospitalLoadError = false;
    const cachedUserNav = this.navigationService.CachedNavigationMenu;
    const homeNavItem = cachedUserNav.getNavigationItemUsingPermID(environment.HomePermId);
    const navSubscription = this.hospitalChangeService.changeHospitalId(environment.HospitalChangeServiceApi, hospid, environment.UseTestHospital)
      .subscribe(hospChangeResult => {
        const result: DataResult = hospChangeResult;
        this.logger.info('app.component', 'handleHospitalDashboardNavigation', this.subscriptionSelected, this.subscriptions, `Facility Change response. Status: ${result.Status}`, '');
        const data = (result.Data && result.Data.message) ? result.Data.message : 'no data';
        if ((result.Ok === true) && (result.Status === 200)) {
          this.logger.info('app.component', 'handleHospitalDashboardNavigation', this.subscriptionSelected, this.subscriptions, `Facility Change message: ${data}`, '');
          this.navigateToSameDomain(routerPath, navigationExtras);
        } else if ((result.Ok === true) && (result.Status === 401)) {
          this.logger.warn('app.component', 'handleHospitalDashboardNavigation', this.subscriptionSelected, this.subscriptions, `Facility Change was not successful. Response Status: ${result.Status}`, '');
          this.logUserOut();
        } else {
          this.logger.error('app.component', 'handleHospitalDashboardNavigation', this.subscriptionSelected, this.subscriptions, `Facility Change was not successful. Response Status: ${result.Status}`, result.StatusText);
          this.logger.error('app.component', 'handleHospitalDashboardNavigation', this.subscriptionSelected, this.subscriptions, `Facility Change message: ${data}`, result.StatusText);
          this.hospitalLoadError = true;
          setTimeout(() => {
            const kv: KeyValue<string, string> = { key: homeNavItem.PermItemID, value: homeNavItem.ItemName };
            this.onCrumbClicked(kv);
          }, 0);
        }
        this.requestFreshToken();
        this.settingMenuItems = false;
      });
    this.$subscriptions.add(navSubscription);
  }

  // Call Subscription Change Service
  private handleSubscriptionChange(navItem: NavigationItem) {
    if (this.IsTimeoutDialogVisible) {
      this.logger.warn('app.component', 'handleSubscriptionChange', this.subscriptionSelected, this.subscriptions,
        'Subscription change attempted while timeout dialog is visible', `navItem: ${navItem.PermItemID}`);
    }
    const menuItems = this.navigationService.CachedMenuItems;
    const menuitem = this.navigationService.getMenuItem(navItem.PermItemID, menuItems);
    const items = menuitem?.items || [];
    const hasItems = items.length > 0;
    const id = hasItems ? items[0].id : '';
    const isStub = hasItems && NavigationUtils.IsStubFromPermId(id);
    const isHospitalItem = hasItems && NavigationUtils.IsLastPartHospitalItemFromPermId(id);
    const displayInline = isHospitalItem && items.length < environment.MaxNumberOfHospitals;
    const childStubExists = (isStub || isHospitalItem) && !displayInline;
    if (this.backButtonClicked === true) {
      this.breadcrumbService.setBreadcrumbs(navItem);
      if (childStubExists) {
        this.displayMenuExtension(navItem);
      } else {
        this.displayBlankPage(navItem);
      }
    } else {
      // Register that a subscription change has been made
      this.settingMenuItems = true;
      this.subscriptionChangeError = false;
      const subid = NavigationUtils.GetSubscriptionIdFromPermId(navItem.PermItemID);
      const cachedUserNav = this.navigationService.CachedNavigationMenu;
      const homeNavItem = cachedUserNav.getNavigationItemUsingPermID(environment.HomePermId);
      const subscription = this.subscriptionChangeService.changeSubscriptionId(environment.TokenServiceApi, subid, environment.UseTestProduct)
        .subscribe(hospChangeResult => {
          const result: DataResult = hospChangeResult;
          this.logger.info('app.component', 'handleSubscriptionChange', this.subscriptionSelected, this.subscriptions, `Subscription Change response. Status: ${result.Status}`, '');
          const data = (result.Data && result.Data.message) ? result.Data.message : 'no data';
          if ((result.Ok === true) && (result.Status === 200)) {
            this.logger.info('app.component', 'handleSubscriptionChange', this.subscriptionSelected, this.subscriptions, `Subscription Change message: ${data}`, '');
            this.subscriptionChangeError = false;
            this.authService.setSubscription(subid.toString());
            this.subscriptionSelected = Number(subid);
            this.subscriptionChangeService.SelectedSubscription = this.subscriptionSelected;
            this.breadcrumbService.setBreadcrumbs(navItem);
            if (childStubExists) {
              this.displayMenuExtension(navItem);
            } else {
              this.displayBlankPage(navItem);
            }
          }
          else if ((result.Ok === true) && (result.Status === 401)) {
            this.logger.warn('app.component', 'handleSubscriptionChange', this.subscriptionSelected, this.subscriptions, `Subscription Change was not successful. Response Status: ${result.Status}`, '');
            this.logUserOut();
          } else {
            this.logger.error('app.component', 'handleSubscriptionChange', this.subscriptionSelected, this.subscriptions, `Subscription Change message: ${data}`, 'Error changing Subscription');
            this.subscriptionChangeError = true;
            setTimeout(() => {
              const kv: KeyValue<string, string> = { key: homeNavItem.PermItemID, value: homeNavItem.ItemName };
              this.onCrumbClicked(kv);
            }, 0);
          }
          this.requestFreshToken();
          this.settingMenuItems = false;
        });
      this.$subscriptions.add(subscription);
    }

  }

  private requestFreshToken() {
    setTimeout(() => {
      this.logger.debug('app.component', 'requestFreshToken', this.subscriptionSelected, this.subscriptions, 'getting fresh token after facility change service response', '');
      this.authService.getNewAccessToken(environment.TokenServiceApi, environment.TokenServiceMedia, environment.UseTestToken, environment.UseCookies, environment.CookieName);
    }, 0);
  }

  private navigateToSameDomain(routerPath: string, navigationExtras: any) {
    const rtr = this.router;
    navigationExtras ? rtr.navigate([routerPath], navigationExtras) : rtr.navigate([routerPath]);
  }

  private routeToDifferentDomain(navItem: NavigationItem) {
    const path = '/differentdomain';
    const routerPath = `if${path}`;
    const url = navItem.Path.toString();
    const params: any = {};
    params.url = url;
    params.linkid = navItem.PermItemID;
    const navigationExtras = {
      queryParams: params
    };
    this.logger.info('app.component', 'routeToDifferentDomain', this.subscriptionSelected, this.subscriptions, `RouterPath:${routerPath}`, '');
    this.router.navigate([routerPath], navigationExtras);
  }

  private routeToHigherLogic(navItem: NavigationItem, communites: string) {
    const path = '/differentdomain';
    const routerPath = `if${path}`;
    const url = navItem.Path.toString();
    const params: any = {};
    params.url = url;
    params.com = communites;
    const navigationExtras = {
      queryParams: params
    };
    this.logger.info('app.component', 'routeToHigherLogic', this.subscriptionSelected, this.subscriptions, `RouterPath:${routerPath}`, '');
    this.router.navigate([routerPath], navigationExtras);
  }

  private handleRouterNavigation(navItem: NavigationItem) {
    this.isUserLoggedIn = this.authService.isUserLoggedIn();
    this.logger.info('app.component', 'handleRouterNavigation', this.subscriptionSelected, this.subscriptions, `isUserLoggedIn=${this.isUserLoggedIn}`, '');
    if (this.isUserLoggedIn === true) {
      // Navigate to the page
      if (navItem === null || navItem === undefined) {
        this.logger.warn('app.component', 'handleNavigation', this.subscriptionSelected, this.subscriptions, 'Navigation item is null. Cannot navigate', '');
      }
      this.displayChildItems = false;
      if (navItem.Path) {
        const fullpath = navItem.Path.toString();
        this.logger.info('app.component', 'handleNavigation', this.subscriptionSelected, this.subscriptions, `IF Domain:${environment.WebServer}`, '');
        this.logger.info('app.component', 'handleNavigation', this.subscriptionSelected, this.subscriptions, `Path Domain:${fullpath}`, '');
        const isDifferentDomain = navItem.Path.isDifferentDomain();
        const isHigherLogicLink = navItem.Path.toString().toLowerCase().includes("higherlogic");
        if (this.backButtonClicked) {
          this.routeToSameDomain(navItem);
        } else if (isHigherLogicLink === true) {
          const communities = this.sessionService.getUserCommunities();
          this.routeToHigherLogic(navItem, communities)
        } else if (isDifferentDomain === true) {
          this.routeToDifferentDomain(navItem);
        } else {
          this.routeToSameDomain(navItem);
        }
      } else {
        this.displayBlankPage(navItem);
      }
    } else {
      // Log the user out
      this.logger.warn('app.component', 'handleRouterNavigation', this.subscriptionSelected, this.subscriptions, `isUserLoggedIn=${this.isUserLoggedIn}. Path=${navItem.Path.toString()}`, '');
      this.logUserOut();
    }
  }

  public slideMenuClick(event: any) {
    this.logger.debug('app.component', 'slideMenuClick', this.subscriptionSelected, this.subscriptions, 'checking Back Button click handler', '');
    const menu = this.slideMenu;
    const back = menu.backwardViewChild;
    if (back && back.nativeElement) {
      const backNative = back.nativeElement;
      if (backNative.onclick === null) {
        this.logger.debug('app.component', 'slideMenuClick', this.subscriptionSelected, this.subscriptions, 'adding Back Button click handler', '');
        backNative.onclick = (e: Event) => this.handleBackButtonEvent(e);
      }
    }
  }

  private onMenuTransitionStart(event: TransitionEvent) {
    if (event.propertyName === 'left') {
      this.menuAnimating = true;
      const { nativeElement } = this.slideMenu.el;
      nativeElement.classList.add('slide-menu-animating');
    }
  }

  private onMenuTransitionEnd(event: TransitionEvent) {
    const animationComplete = () => {
      this.menuAnimating = false;
      const { nativeElement } = this.slideMenu.el;
      nativeElement.classList.remove('slide-menu-animating');
    };
    if (event.propertyName === 'left') {
      setTimeout(animationComplete, 200);
    }
  }

  private checkForSubscriptionChange(navItem: NavigationItem) {
    const cachedNav = this.navigationService.CachedNavigationMenu;
    const availableSubscriptionPermIds = this.navigationMappingService.getAvailableSubscriptionPermIds(cachedNav);
    const isSubPermId = availableSubscriptionPermIds.includes(navItem.PermItemID);
    if (isSubPermId) {
      const currentSubPermId = this.navigationMappingService.mapSubscriptionIdToPermId(cachedNav, this.subscriptionSelected);
      if (currentSubPermId !== navItem.PermItemID) {
        this.logger.info('app.component', 'checkForSubscriptionChange', this.subscriptionSelected, this.subscriptions, `Subscription changed to : ${navItem.PermItemID}`, '');
        const subId = NavigationUtils.GetSubscriptionIdFromPermId(navItem.PermItemID);
        this.subscriptionSelected = +subId;
      }
    }
  }

  private checkForHospitalChange(navItem: NavigationItem) {
    const hospitalId = NavigationUtils.GetHospitalIdNavItem(navItem);
    const sessionHosp = this.sessionService.getUserHospitalId();
    if (hospitalId && hospitalId !== sessionHosp) {
      this.logger.info('app.component', 'checkForHospitalChange', this.subscriptionSelected, this.subscriptions, `Facility changed to : ${hospitalId}`, '');
      this.sessionService.setUserHospitalId(hospitalId);
      const hospLevel = NavigationUtils.IsHospitalLevel(navItem.PermItemID);
      if (hospLevel) {
        const hospName = navItem.ItemName;
        this.sessionService.setUserHospitalName(hospName);
      }
    }
  }

  private displayBlankPage(navItem: NavigationItem) {
    const routerPath = `if/blankpage`;
    const params: any = {};
    params.linkid = (navItem.PermItemID) ? navItem.PermItemID : '';
    const navigationExtras = {
      queryParams: params
    };
    this.logger.debug('app.component', 'displayBlankPage', this.subscriptionSelected, this.subscriptions, `blank page:${routerPath}`, '');
    this.router.navigate([routerPath], navigationExtras);
  }

  private displayMenuExtension(navItem: NavigationItem) {
    const routerPath = `if/menuextension`;
    const params = navItem.Path.toQueryParam();
    params.linkid = navItem.PermItemID;
    const navigationExtras = {
      queryParams: params
    };
    this.logger.debug('app.component', 'displayMenuExtension', this.subscriptionSelected, this.subscriptions, `requires menu extension:${routerPath}`, '');
    this.router.navigate([routerPath], navigationExtras);
  }

  private isHospitalInCache(hospitalNavItem: NavigationItem): boolean {
    const cachedNode = this.navigationService.CachedNavigationMenu?.getNavigationItemUsingPermID(hospitalNavItem.PermItemID);
    return !!(cachedNode?.Items?.length);
  }

  private loadHospital(navItem: NavigationItem) {
    if (navItem && navItem.PermItemID) {
      this.settingMenuItems = true;
      // Clear any cached single-facility data **and** reset current menu flag
      // so loadMenuType(SingleHospital) can run even if we were already on
      // SingleHospital before.
      this.navigationService.clearSingleHospitalCache();
      if (this.navigationService.DisplayedMenuType === NavigationMenuTypeEnum.SingleHospital) {
        this.navigationService.DisplayedMenuType = NavigationMenuTypeEnum.NotSet;
      }
      // Load hospital menu from service
      const hospId = NavigationUtils.GetHospitalIdNavItem(navItem);
      this.loadUserNavigationSingleHospitalMenu(hospId);
    }
  }

  private navigateToItem(navItem: NavigationItem) {
    this.breadcrumbService.setBreadcrumbs(navItem);
    this.handleRouterNavigation(navItem);
  }

  private prepareMenuItemsForNavigation(navItem: NavigationItem): void {
    const cachedUserNav = this.navigationService.CachedNavigationMenu;
    const menuType = this.navigationService.DisplayedMenuType;
    const clonedUserNav = this.navigationService.CloneUserNavigation(cachedUserNav);
    const hospMenuItemsMap = this.navigationService.HospitalMenuItemCache;
    const unitMenuItemsMap = this.navigationService.UnitMenuItemCache;
    const truncatedMenuItems = this.navigationMappingService.getTruncatedMenuItems(
      menuType, clonedUserNav, navItem.PermItemID, hospMenuItemsMap, unitMenuItemsMap
    );
    this.items = this.navigationService.loadOnlyRequiredHospitalsChildItemsFromCache(
      navItem, truncatedMenuItems, this.items, this.subscriptionSelectedPermId, unitMenuItemsMap
    );
    this.navigationService.updateDisplayedNavigationMenu(clonedUserNav);
  }

  private shouldProgrammaticallyAdvanceMenu(navItem: NavigationItem, menuItemId: string): boolean {
    const menuItem = this.navigationService.getMenuItem(menuItemId, this.items);
    const hadChildrenBefore = menuItem?.items?.length > 0;
    return !hadChildrenBefore;
  }

  private onSubscriptionMenuItemClicked(navItem: NavigationItem) {
    this.logger.info('app.component', 'onSubscriptionMenuItemClicked', this.subscriptionSelected, this.subscriptions, `navItem:${navItem.PermItemID}`, '');
    this.handleSubscriptionChange(navItem);
  }

  private onMenuItemClicked = (menuEvent: MenuItemCommandEvent) => {
    const menuItemId = menuEvent.item?.id;
    const displayedNavigationMenu = this.navigationService.DisplayedNavigationMenu;
    const cachedNavigationMenu = this.navigationService.CachedNavigationMenu;
    const isHospStubClick = NavigationUtils.IsHospitalStubFromPermId(menuItemId);
    const isUnitStubClick = NavigationUtils.IsUnitStubFromPermId(menuItemId);
    const isMenuTraverse = this.iframeService.LoadingLinkId !== '';
    
    this.subscriptionChangeError = false;
    this.hospitalLoadError = false;
    
    if (isHospStubClick) {
      const ni = displayedNavigationMenu.getNavigationItemUsingPermID(this.activeLinkId);
      this.navigateToItem(ni);
      return;
    }

    if (isUnitStubClick) {
      if (isMenuTraverse) {
        const selectedUnitItem = this.navigationService.SelectedUnitMenuItem;
        if (selectedUnitItem) {
          this.sessionService.setUnitId(selectedUnitItem.id);
          this.sessionService.setUserUnitName(selectedUnitItem.label);
          const ni = displayedNavigationMenu.getNavigationItemUsingPermID(selectedUnitItem.id);
          const needsAdvance = this.shouldProgrammaticallyAdvanceMenu(ni, selectedUnitItem.id);
          this.prepareMenuItemsForNavigation(ni);
          if (needsAdvance) {
            this.clickMenuItem(ni.PermItemID);
          }
          this.activeLinkId = ni.PermItemID;
        }
      } else {
        const facilityNavItem = cachedNavigationMenu?.getNavigationItemUsingPermID(this.activeLinkId) || displayedNavigationMenu?.getNavigationItemUsingPermID(this.activeLinkId);
        if (facilityNavItem) {
          this.displayMenuExtension(facilityNavItem);
        }
      }
      return;
    }
    
    const navItem = displayedNavigationMenu?.getNavigationItemUsingPermID(menuItemId) || cachedNavigationMenu?.getNavigationItemUsingPermID(menuItemId);
    
    if (!navItem) {
      this.logger.warn('app.component', 'onMenuItemClicked', this.subscriptionSelected, this.subscriptions, 
        `navItem not found for id: ${menuItemId}`, '');
      return;
    }

    const isSubscriptionClick = this.navigationMappingService
      .getAvailableSubscriptionPermIds(cachedNavigationMenu)
      .includes(navItem.PermItemID);
    const isFacilityClick = NavigationUtils.IsLastPartHospitalItem(navItem);
    const isUnitClick = NavigationUtils.IsLastPartUnitItem(navItem);
    
    if (this.backButtonClicked) {
      this.iframeService.LoadingLinkId = navItem.PermItemID;
      this.items = this.navigationService.clearStubChildItems(navItem, this.items);

      const cachedUserNav = this.navigationService.CachedNavigationMenu;
      if (cachedUserNav) {
        const clonedUserNav = this.navigationService.CloneUserNavigation(cachedUserNav);
        this.navigationMappingService.truncateNavMenu(clonedUserNav, navItem.PermItemID);
        this.navigationService.updateDisplayedNavigationMenu(clonedUserNav);
      }
    
      const menuitem = this.navigationService.getMenuItem(navItem.PermItemID, this.items);
      const items = menuitem?.items || [];
      const hasItems = items.length > 0;
      const id = hasItems ? items[0].id : '';
      const isStub = hasItems && NavigationUtils.IsStubFromPermId(id);
      const isHospitalItem = hasItems && NavigationUtils.IsLastPartHospitalItemFromPermId(id);
      const displayInline = isHospitalItem && items.length < environment.MaxNumberOfHospitals;
      const childStubExists = (isStub || isHospitalItem) && !displayInline;
      
      this.breadcrumbService.setBreadcrumbs(navItem);
      if (childStubExists) {
        this.displayMenuExtension(navItem);
      } else {
        this.navigateToItem(navItem);
      }
      return;
    }
    
    if (isSubscriptionClick) {
      this.checkForSubscriptionChange(navItem);
      this.onSubscriptionMenuItemClicked(navItem);
      return;
    }
    
    if (isFacilityClick) {
      this.activeLinkId = navItem.PermItemID;
      this.checkForHospitalChange(navItem);
      this.breadcrumbService.setBreadcrumbs(navItem);
      if (!this.isHospitalInCache(navItem)) {
        this.loadHospital(navItem);
      } else {
        this.prepareMenuItemsForNavigation(navItem);
        this.clickMenuItem(navItem.PermItemID);
        this.navigateToItem(navItem);
      }
      return;
    }
    
    if (isUnitClick) {
      const unitid = NavigationUtils.GetUnitIdFromPermId(navItem.PermItemID);
      this.sessionService.setUnitId(unitid);
      this.sessionService.setUserUnitName(navItem.ItemName);
      
      const needsAdvance = this.shouldProgrammaticallyAdvanceMenu(navItem, menuItemId);
      this.prepareMenuItemsForNavigation(navItem);
      if (needsAdvance) {
        this.clickMenuItem(navItem.PermItemID);
      }
      this.activeLinkId = navItem.PermItemID;
      this.iframeService.LoadingLinkId = navItem.PermItemID;
      this.navigateToItem(navItem);
      return;
    }
    
    this.iframeService.LoadingLinkId = navItem.PermItemID;
    this.navigateToItem(navItem);
  }

async onCrumbClicked($event) {
    const kv = $event as KeyValue<string, string>;
    this.iframeService.LoadingLinkId = kv.key;
    this.logger.info('app.component', 'onCrumbClicked', this.subscriptionSelected, this.subscriptions, `LinkId:${kv.key}`, '');
    const homeCrumbClicked = Breadcrumb.isHomeCrumb(kv.key);
    const cachedNav = this.navigationService.CachedNavigationMenu;
    const displayedNav = this.navigationService.DisplayedNavigationMenu;
    const userNav = homeCrumbClicked ? (cachedNav || displayedNav) : (displayedNav || cachedNav);

    if (!userNav) { return; }

    const navItem =
      userNav.getNavigationItemUsingPermID(kv.key) ||
      cachedNav?.getNavigationItemUsingPermID(kv.key) ||
      displayedNav?.getNavigationItemUsingPermID(kv.key);

    if (!navItem) { return; }
    
    this.finalDestination = (navItem && navItem.Path) ? navItem.Path.Directory : '';
    let lastLinkId = this.breadcrumbService.LastCrumb.Item.key;
    if (homeCrumbClicked) {
      const clonedUserNav = this.navigationService.CloneUserNavigation(userNav);
      const truncatedUserNav = this.navigationMappingService.truncateNavMenu(clonedUserNav, environment.HomePermId);
      this.setDisplayMenu(truncatedUserNav, this.navigationService.DisplayedMenuType, environment.HomePermId, true);
      this.breadcrumbService.clearBreadcrumbs();
      this.breadcrumbService.setHomeBreadcrumb();
    } else if (lastLinkId !== navItem.PermItemID) {
      const navForClickThrough = cachedNav || userNav;
      await this.clickThroughMenu(navForClickThrough, lastLinkId, navItem.PermItemID);
      this.backButtonClicked = false;
      const currentDisplayedNav = this.navigationService.DisplayedNavigationMenu || userNav;
      const containsUnit = navItem.PermItemID.includes('_u');
      if (containsUnit) {
        const unitid = NavigationUtils.GetUnitIdFromPermId(navItem.PermItemID);
        this.sessionService.setUnitId(unitid);
        const unitPermId = navItem.PermItemID.split('_').slice(0, 4).join('_');
        const unitNavItem = currentDisplayedNav.getNavigationItemUsingPermID(unitPermId);
        if (unitNavItem) {
          this.sessionService.setUserUnitName(unitNavItem.ItemName);
        }
      }
      const navItemToNavigate = currentDisplayedNav.getNavigationItemUsingPermID(navItem.PermItemID) || navItem;
      this.navigateToItem(navItemToNavigate);
    }
  }

  async onLinkClicked(navItem: NavigationItem) {
    this.logger.info('app.component', 'onLinkClicked', this.subscriptionSelected, this.subscriptions, `LinkId:${navItem.PermItemID}`, '');
    this.iframeService.LoadingLinkId = navItem.PermItemID;
    const userNav = this.navigationService.DisplayedNavigationMenu;
    let lastLinkId = this.breadcrumbService.LastCrumb.Item.key;
    if (lastLinkId !== navItem.PermItemID) {
      // Step backwards to Hospital dashboard
      const oldParts = lastLinkId.split('_');
      const newParts = navItem.PermItemID.split('_');
      const stepsBack = oldParts.length - 2;
      this.clickMenuBackward(stepsBack);
      // Populate the menu with the unit items
      const cachedUserNav = this.navigationService.CachedNavigationMenu;
      const clonedUserNav = this.navigationService.CloneUserNavigation(cachedUserNav);
      this.navigationService.updateDisplayedNavigationMenu(clonedUserNav);
      const hospMenuItemsMap = this.navigationService.HospitalMenuItemCache;
      const unitMenuItemsMap = this.navigationService.UnitMenuItemCache;
      const menuType = this.navigationService.DisplayedMenuType;
      const truncatedMenuItems = this.navigationMappingService.getTruncatedMenuItems(menuType, clonedUserNav, navItem.PermItemID, hospMenuItemsMap, unitMenuItemsMap);
      this.items = this.navigationService.loadOnlyRequiredHospitalsChildItemsFromCache(navItem, truncatedMenuItems, this.items, this.subscriptionSelectedPermId, unitMenuItemsMap);
      // Now navigate forward from hospital
      const hospLevelLink = `${newParts[0]}_${newParts[1]}`;
      lastLinkId = hospLevelLink;
      await this.clickThroughMenu(userNav, lastLinkId, navItem.PermItemID);
      this.backButtonClicked = false;
      this.navigateToItem(navItem);
    }
  }

  private executeMenuItemCommand(menuItemId: string, $event) {
    const items: Array<any> = this.slideMenu.model;
    const navService = this.navigationService;
    const menuItem = navService.getMenuItem(menuItemId, items) || navService.getMenuItemFromCache(menuItemId);
    if (menuItem) {
      menuItem.command({ item: menuItem, originalEvent: $event });
    }
  }

  private clickMenuBackward(stepsBack: number) {
    this.backButtonClicked = true;
    const back = this.slideMenu.backwardViewChild;
    const backNative = back.nativeElement;
    for (let index = 0; index < stepsBack; index++) {
      backNative.click();
    }
  }

  private logUserOut() {
    setTimeout(() => {
      this.authService.logout();
      this.routeToLoggedOut();
    }, 0);
  }

  private setIframeClass(path: string) {
    let portalClass = '';
    if (path) {
      const isReportPath = path.toLowerCase().includes(this.PORTAL_REPORTS);
      if (isReportPath) {
        portalClass = 'legacy reports';
      }
    }
    this.portalClass = portalClass;
  }

  private routeToLoggedOut() {
    this.router.navigate(['loggedout']);
  }

  onLogoutClicked() {
    this.logUserOut();
  }

  // Helper method to validate iframe URL origins and detect cross-origin issues
  private validateIframeUrl(frameName: string, url: string): void {
    if (/^https?:\/\//i.test(url)) {
      try {
        const parent = new URL(environment.WebServer);
        const child  = new URL(url);
        // Normalize implicit ports (empty string means 80 for http, 443 for https)
        const getPort = (urlObj: URL) => urlObj.port || (urlObj.protocol === 'https:' ? '443' : '80');
        const protocolMatch = parent.protocol === child.protocol;
        const hostMatch = parent.hostname === child.hostname;
        const portMatch = environment.environment === 'Local' || getPort(parent) === getPort(child);
        if (!protocolMatch || !hostMatch || !portMatch) {
          const detail = `Parent: ${parent.origin}, Child: ${child.origin}`;
          this.logger.error('app.component', 'validateIframeUrl', this.subscriptionSelected, this.subscriptions,
            `IFRAME ORIGIN MISMATCH - Frame: ${frameName} - proto:${protocolMatch} host:${hostMatch} port:${portMatch}`, 
            detail);
        }
      } catch (err) {
        this.logger.error('app.component', 'validateIframeUrl', this.subscriptionSelected, this.subscriptions,
          `Invalid URL or origin check failed for ${frameName}: ${url}`, `${err}`);
      }
    }
  }

  // Helper method to safely access iframe contentWindow with error detection
  private safeAccessIframeContentWindow(frameName: string, iframe: HTMLIFrameElement): Window | null {
    if (environment.environment === 'Local') {
      return null;
    }
    try {
      if (!iframe) {
        this.logger.error('app.component', 'safeAccessIframeContentWindow', this.subscriptionSelected, this.subscriptions,
          `Iframe element is null for ${frameName}`, '');
        return null;
      }
      
      const contentWindow = iframe.contentWindow;
      if (!contentWindow) {
        this.logger.error('app.component', 'safeAccessIframeContentWindow', this.subscriptionSelected, this.subscriptions,
          `ContentWindow is null for ${frameName}`, `Iframe src: ${iframe.src}`);
        return null;
      }
      
      // Try to access document to detect cross-origin issues
      try {
        const doc = contentWindow.document;
        if (!doc) {
          this.logger.warn('app.component', 'safeAccessIframeContentWindow', this.subscriptionSelected, this.subscriptions,
            `Document is null for ${frameName}`, `Iframe src: ${iframe.src}`);
        }
      } catch (crossOriginError) {
        this.logger.error('app.component', 'safeAccessIframeContentWindow', this.subscriptionSelected, this.subscriptions,
          `CROSS-ORIGIN ACCESS BLOCKED for ${frameName}`, 
          `Error: ${crossOriginError.message}, Iframe src: ${iframe.src}, Current origin: ${window.location.origin}`);
      }
      
      return contentWindow;
    } catch (error) {
      this.logger.error('app.component', 'safeAccessIframeContentWindow', this.subscriptionSelected, this.subscriptions,
        `Error accessing contentWindow for ${frameName}`, `Error: ${error}`);
      return null;
    }
  }

  collectGarbageForIframe(iframe: HTMLIFrameElement): boolean {
    let clean = false;
    if (iframe) {
      this.logger.info('app.component', 'collectGarbageForIframe', this.subscriptionSelected, this.subscriptions, `Cleaning up iframe: ${iframe.src}`, '');
      iframe.src = 'about:blank';
      iframe.contentWindow.close();
      iframe.parentElement.removeChild(iframe);
      iframe = null;
      clean = true;
    }
    return clean;
  }

  unsubscribe() {
    this.logger.info('app.component', 'unsubscribe', this.subscriptionSelected, this.subscriptions, 'unsubscribing...', '');
    if (this.$subscriptions) {
      this.$subscriptions.unsubscribe();
    }
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    const existingMarker = sessionStorage.getItem(this._RELOAD_TO_HOME_KEY);
    // When user clicks browser refresh, this runs and stores the marker.
    // Set one-time marker before browser refresh/navigation away.
    // On the next app bootstrap, handleNavigationEvent reads this marker.
    sessionStorage.setItem(this._RELOAD_TO_HOME_KEY, '1');
    this.logger.info('app.component', 'onBeforeUnload', this.subscriptionSelected, this.subscriptions,
      `Reload marker set. markerAfterSet=${sessionStorage.getItem(this._RELOAD_TO_HOME_KEY)}`, '');
  }

  async ngOnDestroy() {
    this.unsubscribe();
    // These messages about failing to clean up iframes sometimes appear in the log. It's possible that the iframe doesn't exist on the page
    // at the point when this destroy event fires
    this.logger.info('app.component', 'ngDestroy', this.subscriptionSelected, this.subscriptions, 'Cleaning up iframes', '');
    const indicatorIframe = document.getElementById('appIndicatorIframe') as HTMLIFrameElement;
    const ifClean = this.collectGarbageForIframe(indicatorIframe);
    if (!ifClean) { this.logger.warn('app.component', 'ngDestroy', this.subscriptionSelected, this.subscriptions, 'Failed to clean up Indicator Factory iframe', ''); }
    const validationIframe = document.getElementById('appValidationIFrame') as HTMLIFrameElement;
    const valClean = this.collectGarbageForIframe(validationIframe);
    if (!valClean) { this.logger.warn('app.component', 'ngDestroy', this.subscriptionSelected, this.subscriptions, 'Failed to clean up Validation Engine iframe', ''); }
    const performanceIframe = document.getElementById('appPerformanceIFrame') as HTMLIFrameElement;
    const perfClean = this.collectGarbageForIframe(performanceIframe);
    if (!perfClean) { this.logger.warn('app.component', 'ngDestroy', this.subscriptionSelected, this.subscriptions, 'Failed to clean up Performance Reports iframe', ''); }
    const sysMngIframe = document.getElementById('appSystemManagementIFrame') as HTMLIFrameElement;
    const sysClean = this.collectGarbageForIframe(sysMngIframe);
    if (!sysClean) { this.logger.warn('app.component', 'ngDestroy', this.subscriptionSelected, this.subscriptions, 'Failed to clean up System Management iframe', ''); }
    const ndnqiPublicReportsIFrame = document.getElementById('appNdnqiPublicReportsIFrame') as HTMLIFrameElement;
    // console.log('ngOnDestroy - ndnqiPublicReportsIFrame frame exists: ' + pubFrameExists);
    const pubClean = this.collectGarbageForIframe(ndnqiPublicReportsIFrame);
    // console.log('ngOnDestroy - ndnqiPublicReportsIFrame frame clean:' + pubClean);
    if (!pubClean) { this.logger.warn('app.component', 'ngDestroy', this.subscriptionSelected, this.subscriptions, 'Failed to clean up Public Reports iframe', ''); }
    const portalIframe = document.getElementById('appPortalIframe') as HTMLIFrameElement;
    const portClean = this.collectGarbageForIframe(portalIframe);
    if (!portClean) { this.logger.warn('app.component', 'ngDestroy', this.subscriptionSelected, this.subscriptions, 'Failed to clean up Portal iframe', ''); }
  }

}
